/**
 * Chemistry - Unit Tests, Exercises, and Resources
 * Vanuatu National Curriculum - Years 11-13
 * St Patrick's College
 */

// ============================================
// YEAR 11 CHEMISTRY UNIT TESTS
// ============================================
export const year11ChemistryUnitTests = {
  unit1: {
    id: "y11chem-unit1-test",
    title: "Unit 1: Atomic Structure & Bonding",
    strand: "Atomic Structure, Bonding and Related Properties",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations",
      "Use the Periodic Table provided"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 15,
        questions: [
          { id: 1, question: "The number of protons in an atom is called the:", options: ["A. Mass number", "B. Atomic number", "C. Neutron number", "D. Electron number"], answer: "B", marks: 1 },
          { id: 2, question: "Which subatomic particle has no charge?", options: ["A. Proton", "B. Electron", "C. Neutron", "D. Ion"], answer: "C", marks: 1 },
          { id: 3, question: "The Octet rule states that atoms tend to:", options: ["A. Lose all electrons", "B. Have 8 electrons in outer shell", "C. Have 2 electrons in outer shell", "D. Share all electrons"], answer: "B", marks: 1 },
          { id: 4, question: "Which element has the electronic configuration 2,8,1?", options: ["A. Lithium", "B. Sodium", "C. Potassium", "D. Magnesium"], answer: "B", marks: 1 },
          { id: 5, question: "Ionic bonds are formed by:", options: ["A. Sharing electrons", "B. Transfer of electrons", "C. Sharing protons", "D. Transfer of neutrons"], answer: "B", marks: 1 },
          { id: 6, question: "Covalent bonds are formed between:", options: ["A. Metals only", "B. Non-metals only", "C. Metal and non-metal", "D. Noble gases"], answer: "B", marks: 1 },
          { id: 7, question: "Which has the highest melting point?", options: ["A. NaCl", "B. H₂O", "C. CO₂", "D. CH₄"], answer: "A", marks: 1 },
          { id: 8, question: "The number of valence electrons in oxygen is:", options: ["A. 2", "B. 4", "C. 6", "D. 8"], answer: "C", marks: 1 },
          { id: 9, question: "Isotopes have the same number of:", options: ["A. Neutrons", "B. Protons", "C. Mass number", "D. Electrons and neutrons"], answer: "B", marks: 1 },
          { id: 10, question: "Which is a property of ionic compounds?", options: ["A. Low melting point", "B. Conduct electricity when solid", "C. Conduct electricity when dissolved", "D. Soft and flexible"], answer: "C", marks: 1 },
          { id: 11, question: "The Lewis structure of water shows:", options: ["A. 2 bonding pairs, 2 lone pairs", "B. 2 bonding pairs, 1 lone pair", "C. 3 bonding pairs, 1 lone pair", "D. 4 bonding pairs"], answer: "A", marks: 1 },
          { id: 12, question: "Electronegativity increases:", options: ["A. Down a group", "B. Across a period left to right", "C. Down a period", "D. Across a group right to left"], answer: "B", marks: 1 },
          { id: 13, question: "The formula for magnesium oxide is:", options: ["A. MgO₂", "B. Mg₂O", "C. MgO", "D. Mg₂O₂"], answer: "C", marks: 1 },
          { id: 14, question: "Which element is most reactive?", options: ["A. Na", "B. Mg", "C. Al", "D. K"], answer: "D", marks: 1 },
          { id: 15, question: "A double covalent bond contains:", options: ["A. 2 electrons", "B. 4 electrons", "C. 6 electrons", "D. 8 electrons"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 15,
        questions: [
          { id: 16, question: "Draw and label the structure of a sodium atom (atomic number 11, mass number 23). Include protons, neutrons, and electrons.", marks: 4, answer: "Nucleus: 11 protons, 12 neutrons. Electron shells: 2, 8, 1" },
          { id: 17, question: "Explain why sodium chloride has a high melting point while carbon dioxide has a low melting point.", marks: 4, answer: "NaCl has ionic bonds (strong electrostatic forces between ions) requiring lots of energy to break. CO₂ has weak intermolecular forces between molecules." },
          { id: 18, question: "Write the electronic configuration for: (a) Chlorine (b) Calcium (c) Argon", marks: 3, answer: "(a) 2,8,7 (b) 2,8,8,2 (c) 2,8,8" },
          { id: 19, question: "Draw Lewis structures for: (a) H₂O (b) CO₂", marks: 4, answer: "(a) O with 2 lone pairs, bonded to 2 H atoms. (b) O=C=O with lone pairs on oxygen atoms" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 10,
        questions: [
          { id: 20, question: "Compare and contrast ionic and covalent bonding. Include: (a) How each bond forms (b) Properties of compounds formed (c) Examples of each type (d) Lewis structure examples", marks: 10, answer: "Ionic: electron transfer between metal and non-metal, high MP, conduct when dissolved/molten, e.g., NaCl. Covalent: electron sharing between non-metals, low MP, don't conduct, e.g., H₂O. Include Lewis structures showing electron transfer vs sharing." }
        ]
      }
    ]
  },
  unit2: {
    id: "y11chem-unit2-test",
    title: "Unit 2: The Mole Concept & Stoichiometry",
    strand: "Quantitative and Aqueous Chemistry",
    duration: "50 minutes",
    totalMarks: 45,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations",
      "Use Avogadro's number: 6.02 × 10²³"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "Avogadro's number is:", options: ["A. 6.02 × 10²²", "B. 6.02 × 10²³", "C. 6.02 × 10²⁴", "D. 6.02 × 10²¹"], answer: "B", marks: 1 },
          { id: 2, question: "The molar mass of H₂O is:", options: ["A. 16 g/mol", "B. 17 g/mol", "C. 18 g/mol", "D. 20 g/mol"], answer: "C", marks: 1 },
          { id: 3, question: "How many moles are in 44g of CO₂?", options: ["A. 0.5 mol", "B. 1 mol", "C. 2 mol", "D. 44 mol"], answer: "B", marks: 1 },
          { id: 4, question: "The empirical formula of a compound with 40% C, 6.7% H, 53.3% O is:", options: ["A. CHO", "B. CH₂O", "C. C₂H₄O₂", "D. CHO₂"], answer: "B", marks: 1 },
          { id: 5, question: "In the equation 2H₂ + O₂ → 2H₂O, the mole ratio of H₂:O₂ is:", options: ["A. 1:1", "B. 2:1", "C. 1:2", "D. 2:2"], answer: "B", marks: 1 },
          { id: 6, question: "The limiting reactant is:", options: ["A. The reactant in excess", "B. The reactant that runs out first", "C. The product formed", "D. The catalyst"], answer: "B", marks: 1 },
          { id: 7, question: "Percentage composition of Na in NaCl is approximately:", options: ["A. 23%", "B. 39%", "C. 50%", "D. 61%"], answer: "B", marks: 1 },
          { id: 8, question: "1 mole of any gas at STP occupies:", options: ["A. 22.4 L", "B. 24.0 L", "C. 1 L", "D. 100 L"], answer: "A", marks: 1 },
          { id: 9, question: "The molecular formula is always:", options: ["A. Same as empirical formula", "B. A multiple of empirical formula", "C. Smaller than empirical formula", "D. Unrelated to empirical formula"], answer: "B", marks: 1 },
          { id: 10, question: "How many atoms are in 2 moles of carbon?", options: ["A. 6.02 × 10²³", "B. 1.204 × 10²⁴", "C. 3.01 × 10²³", "D. 12.04 × 10²³"], answer: "B", marks: 1 },
          { id: 11, question: "The molar mass of CaCO₃ is:", options: ["A. 40 g/mol", "B. 60 g/mol", "C. 100 g/mol", "D. 120 g/mol"], answer: "C", marks: 1 },
          { id: 12, question: "Stoichiometry is the study of:", options: ["A. Atomic structure", "B. Quantitative relationships in reactions", "C. Electron configuration", "D. Periodic trends"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Calculations",
        marks: 20,
        questions: [
          { id: 13, question: "Calculate the number of moles in 50g of calcium carbonate (CaCO₃). Show all working.", marks: 4, answer: "Molar mass CaCO₃ = 40 + 12 + (16×3) = 100 g/mol. Moles = 50/100 = 0.5 mol" },
          { id: 14, question: "A compound contains 52.2% carbon, 13.0% hydrogen, and 34.8% oxygen. Determine its empirical formula.", marks: 5, answer: "C: 52.2/12 = 4.35, H: 13.0/1 = 13.0, O: 34.8/16 = 2.175. Ratio = 2:6:1. Empirical formula: C₂H₆O" },
          { id: 15, question: "In the reaction: 2Na + Cl₂ → 2NaCl. If 23g of sodium reacts with excess chlorine, what mass of NaCl is produced?", marks: 5, answer: "Moles Na = 23/23 = 1 mol. From equation: 2 mol Na → 2 mol NaCl. So 1 mol Na → 1 mol NaCl. Mass NaCl = 1 × 58.5 = 58.5g" },
          { id: 16, question: "Calculate the percentage composition of nitrogen in ammonium nitrate (NH₄NO₃).", marks: 3, answer: "Molar mass NH₄NO₃ = 14 + 4 + 14 + 48 = 80 g/mol. Mass of N = 28g. % N = (28/80) × 100 = 35%" },
          { id: 17, question: "How many molecules are in 0.25 moles of water?", marks: 3, answer: "Molecules = 0.25 × 6.02 × 10²³ = 1.505 × 10²³ molecules" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 13,
        questions: [
          { id: 18, question: "In an experiment, 10g of magnesium ribbon is reacted with 20g of hydrochloric acid according to: Mg + 2HCl → MgCl₂ + H₂. (a) Calculate the moles of each reactant. (b) Determine the limiting reactant. (c) Calculate the mass of hydrogen gas produced. (d) Calculate the mass of excess reactant remaining.", marks: 13, answer: "(a) Moles Mg = 10/24 = 0.417 mol, Moles HCl = 20/36.5 = 0.548 mol. (b) Ratio needed 1:2, have 0.417:0.548, HCl is limiting. (c) 0.274 mol H₂ produced = 0.548g. (d) Mg excess = 0.417 - 0.274 = 0.143 mol = 3.43g" }
        ]
      }
    ]
  },
  unit3: {
    id: "y11chem-unit3-test",
    title: "Unit 3: Acids, Bases & Solutions",
    strand: "Quantitative and Aqueous Chemistry",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "A solution with pH 3 is:", options: ["A. Strongly basic", "B. Weakly basic", "C. Neutral", "D. Acidic"], answer: "D", marks: 1 },
          { id: 2, question: "The pH of pure water is:", options: ["A. 0", "B. 7", "C. 14", "D. 1"], answer: "B", marks: 1 },
          { id: 3, question: "Which is a strong acid?", options: ["A. Acetic acid", "B. Citric acid", "C. Hydrochloric acid", "D. Carbonic acid"], answer: "C", marks: 1 },
          { id: 4, question: "Concentration is measured in:", options: ["A. grams", "B. moles", "C. mol/L", "D. liters"], answer: "C", marks: 1 },
          { id: 5, question: "When an acid reacts with a base, the products are:", options: ["A. Salt and water", "B. Salt and hydrogen", "C. Water and oxygen", "D. Acid and metal"], answer: "A", marks: 1 },
          { id: 6, question: "Water is polar because:", options: ["A. It has no charge", "B. Oxygen is more electronegative than hydrogen", "C. It is a gas", "D. It has ionic bonds"], answer: "B", marks: 1 },
          { id: 7, question: "A saturated solution:", options: ["A. Can dissolve more solute", "B. Cannot dissolve any more solute", "C. Has no solute", "D. Is always hot"], answer: "B", marks: 1 },
          { id: 8, question: "Indicators change color based on:", options: ["A. Temperature", "B. Pressure", "C. pH", "D. Volume"], answer: "C", marks: 1 },
          { id: 9, question: "NaOH is an example of:", options: ["A. Acid", "B. Base", "C. Salt", "D. Indicator"], answer: "B", marks: 1 },
          { id: 10, question: "The concentration of a solution with 4 moles in 2 L is:", options: ["A. 2 mol/L", "B. 4 mol/L", "C. 8 mol/L", "D. 0.5 mol/L"], answer: "A", marks: 1 },
          { id: 11, question: "Litmus paper turns red in:", options: ["A. Basic solution", "B. Neutral solution", "C. Acidic solution", "D. Salt solution"], answer: "C", marks: 1 },
          { id: 12, question: "Solubility generally increases with:", options: ["A. Decreasing temperature", "B. Increasing temperature", "C. Decreasing pressure", "D. Adding more solvent"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 16,
        questions: [
          { id: 13, question: "Calculate the concentration of a solution containing 20g of NaOH dissolved in 500mL of water.", marks: 4, answer: "Moles NaOH = 20/40 = 0.5 mol. Volume = 0.5 L. Concentration = 0.5/0.5 = 1 mol/L" },
          { id: 14, question: "Explain why water is called the 'universal solvent' and describe two properties of water related to its polarity.", marks: 4, answer: "Water dissolves many substances due to its polar nature. Properties: 1) High surface tension (hydrogen bonding), 2) High specific heat capacity, 3) Acts as solvent for ionic and polar compounds" },
          { id: 15, question: "Write balanced equations for: (a) HCl + NaOH → (b) H₂SO₄ + KOH →", marks: 4, answer: "(a) HCl + NaOH → NaCl + H₂O (b) H₂SO₄ + 2KOH → K₂SO₄ + 2H₂O" },
          { id: 16, question: "Describe what happens to pH when: (a) An acid is diluted (b) A base is added to an acid", marks: 4, answer: "(a) pH increases towards 7 as acid becomes less concentrated. (b) pH increases as base neutralizes acid" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 12,
        questions: [
          { id: 17, question: "Design an experiment to determine if an unknown solution is acidic, basic, or neutral. Include: (a) Materials needed (b) Procedure (c) Expected results (d) Safety precautions", marks: 12, answer: "Materials: Unknown solution, pH paper/indicator, safety glasses, gloves. Procedure: 1) Wear safety equipment, 2) Dip pH paper in solution, 3) Compare color to pH scale. Results: Red = acidic (pH < 7), Green = neutral (pH 7), Blue = basic (pH > 7). Safety: Wear gloves, don't taste chemicals, wash hands." }
        ]
      }
    ]
  },
  unit4: {
    id: "y11chem-unit4-test",
    title: "Unit 4: Energy & Reaction Rates",
    strand: "Physical Chemistry",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Draw diagrams where appropriate"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "An exothermic reaction:", options: ["A. Absorbs heat", "B. Releases heat", "C. Has no energy change", "D. Requires a catalyst"], answer: "B", marks: 1 },
          { id: 2, question: "Activation energy is:", options: ["A. Energy released in reaction", "B. Minimum energy to start reaction", "C. Total energy of products", "D. Heat of combustion"], answer: "B", marks: 1 },
          { id: 3, question: "Collision theory states that reactions occur when:", options: ["A. Molecules are heated", "B. Particles collide with sufficient energy and correct orientation", "C. Catalysts are added", "D. Pressure increases"], answer: "B", marks: 1 },
          { id: 4, question: "Increasing temperature increases reaction rate because:", options: ["A. Molecules slow down", "B. More collisions occur with sufficient energy", "C. Activation energy increases", "D. Products form slower"], answer: "B", marks: 1 },
          { id: 5, question: "A catalyst:", options: ["A. Increases activation energy", "B. Decreases activation energy", "C. Changes products formed", "D. Is used up in reaction"], answer: "B", marks: 1 },
          { id: 6, question: "Surface area affects reaction rate because:", options: ["A. Larger surface = fewer collisions", "B. Larger surface = more collisions", "C. Surface area doesn't affect rate", "D. Only affects gases"], answer: "B", marks: 1 },
          { id: 7, question: "The greenhouse effect is caused by:", options: ["A. Oxygen", "B. Nitrogen", "C. Carbon dioxide and methane", "D. Helium"], answer: "C", marks: 1 },
          { id: 8, question: "Ozone protects Earth from:", options: ["A. Infrared radiation", "B. Ultraviolet radiation", "C. Visible light", "D. Radio waves"], answer: "B", marks: 1 },
          { id: 9, question: "In a closed system:", options: ["A. Matter can enter and leave", "B. Energy can enter and leave", "C. Neither matter nor energy can enter or leave", "D. Only matter can leave"], answer: "C", marks: 1 },
          { id: 10, question: "Dynamic equilibrium occurs when:", options: ["A. Reaction stops", "B. Forward and reverse rates are equal", "C. All reactants are used", "D. Temperature is constant"], answer: "B", marks: 1 },
          { id: 11, question: "Combustion reactions always produce:", options: ["A. Oxygen", "B. Water and carbon dioxide", "C. Hydrogen", "D. Nitrogen"], answer: "B", marks: 1 },
          { id: 12, question: "Potential energy is:", options: ["A. Energy of motion", "B. Stored energy", "C. Heat energy", "D. Light energy"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 16,
        questions: [
          { id: 13, question: "Explain THREE factors that affect the rate of a chemical reaction and give an example of each.", marks: 6, answer: "1) Temperature - higher temp = faster rate (cooking food faster at higher heat). 2) Concentration - higher concentration = faster rate (stronger acid reacts faster). 3) Surface area - more surface = faster rate (powdered sugar dissolves faster than cubes)" },
          { id: 14, question: "Draw an energy diagram for an exothermic reaction, labeling: reactants, products, activation energy, and energy released.", marks: 5, answer: "Diagram showing: Y-axis = Energy, X-axis = Reaction progress. Reactants higher than products. Activation energy hump. Energy released = difference between reactants and products." },
          { id: 15, question: "Explain how carbon dioxide contributes to climate change.", marks: 5, answer: "CO₂ is a greenhouse gas. It absorbs infrared radiation from Earth's surface, trapping heat in the atmosphere. Increased CO₂ from burning fossil fuels leads to global warming and climate change." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 12,
        questions: [
          { id: 16, question: "A student investigates factors affecting reaction rate between magnesium and hydrochloric acid. Design an experiment to test ONE factor, including: (a) Hypothesis (b) Variables (controlled, independent, dependent) (c) Method (d) How to measure rate (e) Expected results", marks: 12, answer: "Testing temperature effect. Hypothesis: Higher temperature increases reaction rate. Variables: Independent - temperature, Dependent - time for Mg to dissolve, Controlled - acid concentration, Mg size. Method: React same size Mg with same acid at different temps. Measure time to dissolve. Results: Higher temp = faster reaction (less time)." }
        ]
      }
    ]
  },
  unit5: {
    id: "y11chem-unit5-test",
    title: "Unit 5: Oxidation & Reduction",
    strand: "Oxidation and Reduction",
    duration: "40 minutes",
    totalMarks: 35,
    instructions: [
      "Answer ALL questions",
      "Use the activity series provided"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 10,
        questions: [
          { id: 1, question: "Oxidation involves:", options: ["A. Gain of electrons", "B. Loss of electrons", "C. Gain of protons", "D. Loss of protons"], answer: "B", marks: 1 },
          { id: 2, question: "Reduction involves:", options: ["A. Gain of electrons", "B. Loss of electrons", "C. Gain of oxygen", "D. Loss of hydrogen"], answer: "A", marks: 1 },
          { id: 3, question: "In the reaction Zn + Cu²⁺ → Zn²⁺ + Cu, zinc is:", options: ["A. Reduced", "B. Oxidized", "C. Unchanged", "D. A catalyst"], answer: "B", marks: 1 },
          { id: 4, question: "A reducing agent:", options: ["A. Gains electrons", "B. Loses electrons", "C. Gains protons", "D. Is always a metal"], answer: "B", marks: 1 },
          { id: 5, question: "Corrosion is an example of:", options: ["A. Reduction only", "B. Oxidation only", "C. Redox reaction", "D. Acid-base reaction"], answer: "C", marks: 1 },
          { id: 6, question: "The most reactive metal in the activity series is:", options: ["A. Gold", "B. Copper", "C. Iron", "D. Potassium"], answer: "D", marks: 1 },
          { id: 7, question: "Rusting of iron requires:", options: ["A. Oxygen only", "B. Water only", "C. Oxygen and water", "D. Carbon dioxide"], answer: "C", marks: 1 },
          { id: 8, question: "In a single displacement reaction:", options: ["A. Two compounds exchange ions", "B. One element replaces another", "C. Elements combine", "D. Compounds decompose"], answer: "B", marks: 1 },
          { id: 9, question: "Combustion is a type of:", options: ["A. Reduction", "B. Oxidation", "C. Decomposition", "D. Synthesis"], answer: "B", marks: 1 },
          { id: 10, question: "Galvanizing prevents rust by:", options: ["A. Removing oxygen", "B. Coating with zinc", "C. Heating the iron", "D. Adding water"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 15,
        questions: [
          { id: 11, question: "Define oxidation and reduction in terms of: (a) Oxygen (b) Hydrogen (c) Electrons", marks: 6, answer: "(a) Oxidation = gain of oxygen, Reduction = loss of oxygen. (b) Oxidation = loss of hydrogen, Reduction = gain of hydrogen. (c) Oxidation = loss of electrons, Reduction = gain of electrons (OIL RIG)" },
          { id: 12, question: "Write the ionic equation for the reaction between zinc metal and copper sulfate solution. Identify the oxidizing and reducing agents.", marks: 5, answer: "Zn(s) + Cu²⁺(aq) → Zn²⁺(aq) + Cu(s). Zn is reducing agent (loses electrons, is oxidized). Cu²⁺ is oxidizing agent (gains electrons, is reduced)" },
          { id: 13, question: "Explain TWO methods used to prevent rusting and how each method works.", marks: 4, answer: "1) Painting - creates barrier preventing oxygen and water from reaching iron. 2) Galvanizing - zinc coating acts as sacrificial metal, oxidizes instead of iron. Also: oiling, plating with unreactive metal" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 10,
        questions: [
          { id: 14, question: "Using the activity series, predict whether the following reactions will occur. Write balanced equations for those that do occur and explain why some reactions don't happen: (a) Zn + CuSO₄ (b) Cu + ZnSO₄ (c) Fe + AgNO₃ (d) Ag + FeSO₄", marks: 10, answer: "(a) Yes - Zn + CuSO₄ → ZnSO₄ + Cu (Zn more reactive than Cu). (b) No - Cu less reactive than Zn, can't displace it. (c) Yes - Fe + 2AgNO₃ → Fe(NO₃)₂ + 2Ag (Fe more reactive than Ag). (d) No - Ag less reactive than Fe." }
        ]
      }
    ]
  },
  unit6: {
    id: "y11chem-unit6-test",
    title: "Unit 6: Introduction to Organic Chemistry",
    strand: "Organic Chemistry",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Draw structures clearly",
      "Name compounds using IUPAC rules"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "Organic chemistry is the study of compounds containing:", options: ["A. Oxygen", "B. Carbon", "C. Nitrogen", "D. Hydrogen"], answer: "B", marks: 1 },
          { id: 2, question: "The simplest alkane is:", options: ["A. Ethane", "B. Propane", "C. Methane", "D. Butane"], answer: "C", marks: 1 },
          { id: 3, question: "The general formula for alkanes is:", options: ["A. CₙH₂ₙ", "B. CₙH₂ₙ₊₂", "C. CₙH₂ₙ₋₂", "D. CₙHₙ"], answer: "B", marks: 1 },
          { id: 4, question: "Hydrocarbons contain only:", options: ["A. Carbon and oxygen", "B. Carbon and hydrogen", "C. Hydrogen and oxygen", "D. Carbon, hydrogen and oxygen"], answer: "B", marks: 1 },
          { id: 5, question: "Alkenes contain:", options: ["A. Single bonds only", "B. At least one double bond", "C. Triple bonds", "D. No carbon bonds"], answer: "B", marks: 1 },
          { id: 6, question: "The molecular formula of ethene is:", options: ["A. C₂H₆", "B. C₂H₄", "C. C₂H₂", "D. CH₄"], answer: "B", marks: 1 },
          { id: 7, question: "Isomers have the same:", options: ["A. Structure", "B. Molecular formula", "C. Properties", "D. Name"], answer: "B", marks: 1 },
          { id: 8, question: "A functional group is:", options: ["A. A carbon chain", "B. A group of atoms determining properties", "C. A type of hydrocarbon", "D. A carbon-hydrogen bond"], answer: "B", marks: 1 },
          { id: 9, question: "The functional group -OH is called:", options: ["A. Aldehyde", "B. Ketone", "C. Hydroxyl/Alcohol", "D. Carboxylic acid"], answer: "C", marks: 1 },
          { id: 10, question: "Combustion of hydrocarbons produces:", options: ["A. Oxygen only", "B. Water only", "C. Carbon dioxide and water", "D. Hydrogen only"], answer: "C", marks: 1 },
          { id: 11, question: "Which is a saturated hydrocarbon?", options: ["A. Ethene", "B. Propene", "C. Butane", "D. Ethyne"], answer: "C", marks: 1 },
          { id: 12, question: "Crude oil is separated by:", options: ["A. Filtration", "B. Fractional distillation", "C. Evaporation", "D. Crystallization"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 16,
        questions: [
          { id: 13, question: "Draw the structural formula for: (a) Methane (b) Ethane (c) Propane", marks: 6, answer: "(a) CH₄ with H atoms around central C. (b) C₂H₆ with 2 carbons bonded, 3 H on each C. (c) C₃H₈ with 3 carbons in chain, appropriate H atoms" },
          { id: 14, question: "Name the following compounds: (a) CH₃-CH₂-CH₃ (b) CH₂=CH₂ (c) CH₃-OH", marks: 3, answer: "(a) Propane (b) Ethene (c) Methanol" },
          { id: 15, question: "Explain the difference between alkanes and alkenes with examples.", marks: 4, answer: "Alkanes: saturated hydrocarbons with single bonds only (e.g., methane CH₄, ethane C₂H₆). Alkenes: unsaturated hydrocarbons with at least one C=C double bond (e.g., ethene C₂H₄, propene C₃H₆)" },
          { id: 16, question: "Write the balanced equation for the complete combustion of methane.", marks: 3, answer: "CH₄ + 2O₂ → CO₂ + 2H₂O" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 12,
        questions: [
          { id: 17, question: "Crude oil is a mixture of hydrocarbons. (a) Explain how fractional distillation works to separate crude oil (b) Name THREE fractions obtained and their uses (c) Discuss the environmental impact of burning fossil fuels", marks: 12, answer: "(a) Crude oil heated, vapors rise up column, condense at different heights based on boiling points - lower boiling point = higher up. (b) Petroleum gas - fuel/cooking, Gasoline - vehicle fuel, Kerosene - jet fuel, Diesel - trucks/buses, Bitumen - roads. (c) CO₂ contributes to greenhouse effect/climate change, SO₂ causes acid rain, particulates cause air pollution/health issues, oil spills damage ecosystems." }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 11 CHEMISTRY EXERCISES
// ============================================
export const year11ChemistryExercises = {
  unit1: {
    unitTitle: "Unit 1: Atomic Structure & Bonding",
    exercises: [
      {
        id: "ex-y11chem-1-1",
        title: "Exercise 1.1: Atomic Structure Drawing",
        type: "practical",
        instructions: "Draw the atomic structure for the first 20 elements showing electron arrangement.",
        tasks: [
          "Draw Bohr models for elements 1-10 (H to Ne)",
          "Draw Bohr models for elements 11-20 (Na to Ca)",
          "Label protons, neutrons, and electron shells",
          "Write electronic configuration for each element",
          "Identify which elements are in the same group and explain why"
        ],
        materials: "Periodic table, drawing paper, colored pencils"
      },
      {
        id: "ex-y11chem-1-2",
        title: "Exercise 1.2: Lewis Structures Practice",
        type: "written",
        instructions: "Draw Lewis dot structures for various molecules and ions.",
        tasks: [
          "Draw Lewis structures for: H₂, O₂, N₂, HCl, H₂O, NH₃, CH₄",
          "Draw Lewis structures for ionic compounds: NaCl, MgO, CaF₂",
          "Identify bonding pairs and lone pairs in each structure",
          "Determine if bonds are single, double, or triple",
          "Predict shapes of molecules based on electron pairs"
        ],
        materials: "Periodic table, worksheet"
      },
      {
        id: "ex-y11chem-1-3",
        title: "Exercise 1.3: Ionic vs Covalent Properties",
        type: "practical",
        instructions: "Test and compare properties of ionic and covalent compounds.",
        tasks: [
          "Test conductivity of NaCl (solid vs dissolved)",
          "Test conductivity of sugar solution",
          "Compare melting points (using data tables)",
          "Test solubility in water and oil",
          "Record observations and draw conclusions about bonding types"
        ],
        materials: "NaCl, sugar, conductivity meter, water, vegetable oil, beakers"
      }
    ]
  },
  unit2: {
    unitTitle: "Unit 2: The Mole Concept & Stoichiometry",
    exercises: [
      {
        id: "ex-y11chem-2-1",
        title: "Exercise 2.1: Mole Calculations Worksheet",
        type: "written",
        instructions: "Practice converting between mass, moles, and number of particles.",
        tasks: [
          "Calculate moles from mass for 10 different compounds",
          "Calculate mass from moles for 10 different compounds",
          "Calculate number of particles from moles",
          "Calculate molar mass of complex compounds",
          "Solve word problems involving mole conversions"
        ],
        materials: "Calculator, periodic table, worksheet"
      },
      {
        id: "ex-y11chem-2-2",
        title: "Exercise 2.2: Empirical Formula Determination",
        type: "practical",
        instructions: "Determine empirical formulas from experimental data.",
        tasks: [
          "Calculate empirical formula from percentage composition data",
          "Perform magnesium oxide experiment to determine formula",
          "Calculate molecular formula when given molar mass",
          "Compare experimental results to theoretical values",
          "Calculate percentage error in experiment"
        ],
        materials: "Magnesium ribbon, crucible, Bunsen burner, balance, calculator"
      },
      {
        id: "ex-y11chem-2-3",
        title: "Exercise 2.3: Stoichiometry Problems",
        type: "written",
        instructions: "Solve stoichiometry problems using balanced equations.",
        tasks: [
          "Balance chemical equations",
          "Calculate amounts of products from given reactants",
          "Identify limiting reactants in reactions",
          "Calculate theoretical yield",
          "Calculate percentage yield from experimental data"
        ],
        materials: "Calculator, periodic table, problem set"
      }
    ]
  },
  unit3: {
    unitTitle: "Unit 3: Acids, Bases & Solutions",
    exercises: [
      {
        id: "ex-y11chem-3-1",
        title: "Exercise 3.1: pH Testing",
        type: "practical",
        instructions: "Test pH of various household substances.",
        tasks: [
          "Test pH of 10 household substances using indicators",
          "Classify substances as acidic, basic, or neutral",
          "Create a pH scale chart with tested substances",
          "Compare universal indicator to pH paper results",
          "Explain observations using acid-base theory"
        ],
        materials: "pH paper, universal indicator, various household substances, safety equipment"
      },
      {
        id: "ex-y11chem-3-2",
        title: "Exercise 3.2: Concentration Calculations",
        type: "written",
        instructions: "Practice concentration calculations and dilutions.",
        tasks: [
          "Calculate concentration from mass and volume",
          "Calculate mass needed for specific concentrations",
          "Perform dilution calculations (C₁V₁ = C₂V₂)",
          "Convert between different concentration units",
          "Solve word problems involving solutions"
        ],
        materials: "Calculator, worksheet"
      },
      {
        id: "ex-y11chem-3-3",
        title: "Exercise 3.3: Neutralization Reactions",
        type: "practical",
        instructions: "Perform and observe neutralization reactions.",
        tasks: [
          "React HCl with NaOH and measure temperature change",
          "Titrate acid with base using indicator",
          "Write equations for neutralization reactions",
          "Calculate concentration of unknown acid/base",
          "Graph titration curve and identify equivalence point"
        ],
        materials: "HCl, NaOH, phenolphthalein, burette, pipette, conical flask, thermometer"
      }
    ]
  },
  unit4: {
    unitTitle: "Unit 4: Energy & Reaction Rates",
    exercises: [
      {
        id: "ex-y11chem-4-1",
        title: "Exercise 4.1: Reaction Rate Investigation",
        type: "practical",
        instructions: "Investigate factors affecting reaction rate.",
        tasks: [
          "Test effect of temperature on reaction rate",
          "Test effect of concentration on reaction rate",
          "Test effect of surface area on reaction rate",
          "Record time measurements and calculate rates",
          "Graph results and draw conclusions"
        ],
        materials: "Magnesium ribbon, HCl (different concentrations), thermometer, stopwatch, measuring cylinder"
      },
      {
        id: "ex-y11chem-4-2",
        title: "Exercise 4.2: Energy Diagrams",
        type: "written",
        instructions: "Draw and interpret energy diagrams.",
        tasks: [
          "Draw energy diagrams for exothermic reactions",
          "Draw energy diagrams for endothermic reactions",
          "Label activation energy, reactants, products, ΔH",
          "Show effect of catalyst on energy diagram",
          "Interpret given energy diagrams and describe reactions"
        ],
        materials: "Graph paper, ruler, colored pencils"
      },
      {
        id: "ex-y11chem-4-3",
        title: "Exercise 4.3: Climate Chemistry Research",
        type: "research",
        instructions: "Research and present on atmospheric chemistry topics.",
        tasks: [
          "Research greenhouse gases and their sources",
          "Investigate ozone layer depletion and recovery",
          "Analyze local and global climate data",
          "Present findings on one topic to class",
          "Propose solutions to reduce emissions"
        ],
        materials: "Internet access, library resources, presentation software"
      }
    ]
  },
  unit5: {
    unitTitle: "Unit 5: Oxidation & Reduction",
    exercises: [
      {
        id: "ex-y11chem-5-1",
        title: "Exercise 5.1: Redox Reactions Lab",
        type: "practical",
        instructions: "Observe and identify redox reactions.",
        tasks: [
          "React zinc with copper sulfate solution",
          "React iron with copper sulfate solution",
          "Test metals in the activity series",
          "Identify oxidation and reduction in each reaction",
          "Write half-equations for each reaction"
        ],
        materials: "Zinc, iron, copper, copper sulfate solution, test tubes, safety equipment"
      },
      {
        id: "ex-y11chem-5-2",
        title: "Exercise 5.2: Corrosion Prevention",
        type: "practical",
        instructions: "Investigate methods of preventing rusting.",
        tasks: [
          "Set up rust prevention experiment with 5 different conditions",
          "Observe iron nails over 1 week",
          "Compare effectiveness of different methods",
          "Photograph and document observations",
          "Write conclusion explaining results"
        ],
        materials: "Iron nails, oil, paint, zinc, water, air-tight containers, salt"
      },
      {
        id: "ex-y11chem-5-3",
        title: "Exercise 5.3: Electrochemical Cells",
        type: "practical",
        instructions: "Build and test simple electrochemical cells.",
        tasks: [
          "Build a lemon battery",
          "Measure voltage of different metal combinations",
          "Build a zinc-copper cell",
          "Explain how the cell produces electricity",
          "Research applications of electrochemical cells"
        ],
        materials: "Lemons, zinc and copper electrodes, voltmeter, wires, LED"
      }
    ]
  },
  unit6: {
    unitTitle: "Unit 6: Introduction to Organic Chemistry",
    exercises: [
      {
        id: "ex-y11chem-6-1",
        title: "Exercise 6.1: Drawing Organic Structures",
        type: "written",
        instructions: "Practice drawing structural formulas for organic compounds.",
        tasks: [
          "Draw structural formulas for alkanes C1-C5",
          "Draw structural formulas for alkenes C2-C4",
          "Identify and circle functional groups",
          "Name compounds using IUPAC nomenclature",
          "Draw structural isomers of butane"
        ],
        materials: "Worksheet, molecular model kit (optional)"
      },
      {
        id: "ex-y11chem-6-2",
        title: "Exercise 6.2: Hydrocarbon Properties",
        type: "practical",
        instructions: "Investigate properties of hydrocarbons.",
        tasks: [
          "Compare solubility of hydrocarbons in water vs oil",
          "Test combustion of different alkanes",
          "Compare burning characteristics (flame color, smoke)",
          "Test reactivity with bromine water (alkane vs alkene)",
          "Record observations and explain differences"
        ],
        materials: "Hexane, cyclohexene, water, vegetable oil, bromine water, Bunsen burner, test tubes"
      },
      {
        id: "ex-y11chem-6-3",
        title: "Exercise 6.3: Crude Oil Research",
        type: "research",
        instructions: "Research and present on petroleum industry.",
        tasks: [
          "Research fractional distillation process",
          "Identify major fractions and their uses",
          "Investigate environmental impacts of fossil fuels",
          "Research alternative fuels and their advantages",
          "Create presentation or poster on findings"
        ],
        materials: "Internet access, library resources, presentation software"
      }
    ]
  }
};

// ============================================
// CHEMISTRY RESOURCES
// ============================================
export const chemistryResources = [
  {
    id: "chem-res-1",
    title: "Periodic Table (Color-coded)",
    type: "Reference Sheet",
    unit: "All Units",
    year: "Years 11-13",
    description: "Full periodic table with atomic numbers, masses, and electron configurations"
  },
  {
    id: "chem-res-2",
    title: "Activity Series of Metals",
    type: "Reference Sheet",
    unit: "Unit 5",
    year: "Year 11",
    description: "Reactivity series from most to least reactive metals"
  },
  {
    id: "chem-res-3",
    title: "Solubility Rules",
    type: "Reference Sheet",
    unit: "Unit 3",
    year: "Year 11",
    description: "Rules for predicting solubility of ionic compounds"
  },
  {
    id: "chem-res-4",
    title: "Common Polyatomic Ions",
    type: "Reference Sheet",
    unit: "Units 1-2",
    year: "Years 11-12",
    description: "List of common polyatomic ions with charges"
  },
  {
    id: "chem-res-5",
    title: "Mole Calculation Formula Sheet",
    type: "Formula Sheet",
    unit: "Unit 2",
    year: "Year 11",
    description: "All formulas for mole, mass, concentration calculations"
  },
  {
    id: "chem-res-6",
    title: "pH Scale Poster",
    type: "Visual Aid",
    unit: "Unit 3",
    year: "Year 11",
    description: "pH scale with common substances and indicator colors"
  },
  {
    id: "chem-res-7",
    title: "Bonding Types Comparison Chart",
    type: "Comparison Chart",
    unit: "Unit 1",
    year: "Year 11",
    description: "Ionic vs covalent vs metallic bonding comparison"
  },
  {
    id: "chem-res-8",
    title: "Lewis Structure Guide",
    type: "Tutorial",
    unit: "Unit 1",
    year: "Year 11",
    description: "Step-by-step guide to drawing Lewis structures"
  },
  {
    id: "chem-res-9",
    title: "Stoichiometry Problem-Solving Steps",
    type: "Guidelines",
    unit: "Unit 2",
    year: "Year 11",
    description: "Step-by-step method for solving stoichiometry problems"
  },
  {
    id: "chem-res-10",
    title: "Safety Data Sheets (Common Lab Chemicals)",
    type: "Safety Reference",
    unit: "All Units",
    year: "Years 11-13",
    description: "Safety information for common laboratory chemicals"
  },
  {
    id: "chem-res-11",
    title: "Energy Diagram Templates",
    type: "Template",
    unit: "Unit 4",
    year: "Year 11",
    description: "Blank templates for drawing energy diagrams"
  },
  {
    id: "chem-res-12",
    title: "Redox Reaction Identifier",
    type: "Quick Reference",
    unit: "Unit 5",
    year: "Year 11",
    description: "OIL RIG mnemonic and half-equation writing guide"
  }
];

// ============================================
// CHEMISTRY TEST BLUEPRINTS
// ============================================
export const chemistryTestBlueprints = {
  year11: {
    unit1: {
      unitTitle: "Unit 1: Atomic Structure & Bonding",
      strand: "Atomic Structure, Bonding and Related Properties",
      duration: "45 minutes",
      totalMarks: 40,
      slosCovered: ["Atomic structure", "Electronic configuration", "Ionic bonding", "Covalent bonding", "Lewis structures"],
      breakdown: {
        multipleChoice: { count: 15, marks: 15, percentage: 37.5 },
        shortAnswer: { count: 4, marks: 15, percentage: 37.5 },
        extendedResponse: { count: 1, marks: 10, percentage: 25 }
      }
    },
    unit2: {
      unitTitle: "Unit 2: The Mole Concept & Stoichiometry",
      strand: "Quantitative and Aqueous Chemistry",
      duration: "50 minutes",
      totalMarks: 45,
      slosCovered: ["Mole concept", "Molar mass", "Empirical formula", "Stoichiometry", "Limiting reactants"],
      breakdown: {
        multipleChoice: { count: 12, marks: 12, percentage: 27 },
        calculations: { count: 5, marks: 20, percentage: 44 },
        extendedResponse: { count: 1, marks: 13, percentage: 29 }
      }
    },
    unit3: {
      unitTitle: "Unit 3: Acids, Bases & Solutions",
      strand: "Quantitative and Aqueous Chemistry",
      duration: "45 minutes",
      totalMarks: 40,
      slosCovered: ["pH scale", "Acids and bases", "Concentration", "Neutralization", "Water properties"],
      breakdown: {
        multipleChoice: { count: 12, marks: 12, percentage: 30 },
        shortAnswer: { count: 4, marks: 16, percentage: 40 },
        extendedResponse: { count: 1, marks: 12, percentage: 30 }
      }
    },
    unit4: {
      unitTitle: "Unit 4: Energy & Reaction Rates",
      strand: "Physical Chemistry",
      duration: "45 minutes",
      totalMarks: 40,
      slosCovered: ["Collision theory", "Activation energy", "Factors affecting rate", "Energy diagrams", "Equilibrium"],
      breakdown: {
        multipleChoice: { count: 12, marks: 12, percentage: 30 },
        shortAnswer: { count: 3, marks: 16, percentage: 40 },
        extendedResponse: { count: 1, marks: 12, percentage: 30 }
      }
    },
    unit5: {
      unitTitle: "Unit 5: Oxidation & Reduction",
      strand: "Oxidation and Reduction",
      duration: "40 minutes",
      totalMarks: 35,
      slosCovered: ["Oxidation", "Reduction", "Redox reactions", "Activity series", "Corrosion"],
      breakdown: {
        multipleChoice: { count: 10, marks: 10, percentage: 28.5 },
        shortAnswer: { count: 3, marks: 15, percentage: 43 },
        extendedResponse: { count: 1, marks: 10, percentage: 28.5 }
      }
    }
  }
};

// ============================================
// CHEMISTRY ASSESSMENT STRUCTURE
// ============================================
export const chemistryAssessment = {
  year11: {
    internalAssessment: {
      weight: 40,
      components: [
        { name: "Practical Reports", weight: 15, count: 3 },
        { name: "Unit Tests", weight: 15, count: 4 },
        { name: "Research Project", weight: 10, count: 1 }
      ]
    },
    externalExamination: {
      weight: 60,
      format: "2.5 hour written examination",
      sections: "All sections compulsory",
      strandWeights: {
        strand1: 25,
        strand2: 30,
        strand3: 25,
        strand4: 20
      }
    },
    skillLevels: {
      knowledge: "Basic recall of facts, definitions, and formulas",
      comprehension: "Understanding concepts and explaining processes",
      application: "Using knowledge to solve problems and calculations",
      analysis: "Interpreting data, designing experiments, evaluating results"
    }
  }
};

// ============================================
// YEAR 12 CHEMISTRY UNIT TESTS
// ============================================
export const year12ChemistryUnitTests = {
  unit1: {
    id: "y12chem-unit1-test",
    title: "Unit 1: Ionic Compounds & Periodic Trends",
    strand: "Atomic Structure, Bonding and Related Properties",
    duration: "50 minutes",
    totalMarks: 45,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations",
      "Use the Periodic Table provided"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 15,
        questions: [
          { id: 1, question: "Which ion has the electronic configuration 2,8,8?", options: ["A. Na⁺", "B. K⁺", "C. Ca²⁺", "D. Mg²⁺"], answer: "B", marks: 1 },
          { id: 2, question: "The formula for aluminium oxide is:", options: ["A. AlO", "B. Al₂O", "C. Al₂O₃", "D. AlO₃"], answer: "C", marks: 1 },
          { id: 3, question: "Ionization energy generally increases:", options: ["A. Down a group", "B. Across a period left to right", "C. Down a period", "D. Across a group"], answer: "B", marks: 1 },
          { id: 4, question: "Which has the largest ionic radius?", options: ["A. Na⁺", "B. Mg²⁺", "C. O²⁻", "D. F⁻"], answer: "C", marks: 1 },
          { id: 5, question: "The percentage abundance of ³⁵Cl is 75% and ³⁷Cl is 25%. The relative atomic mass is:", options: ["A. 35.0", "B. 35.5", "C. 36.0", "D. 36.5"], answer: "B", marks: 1 },
          { id: 6, question: "Electronegativity is highest for:", options: ["A. Fluorine", "B. Chlorine", "C. Oxygen", "D. Nitrogen"], answer: "A", marks: 1 },
          { id: 7, question: "Intermolecular forces include all EXCEPT:", options: ["A. London dispersion forces", "B. Hydrogen bonding", "C. Ionic bonding", "D. Dipole-dipole forces"], answer: "C", marks: 1 },
          { id: 8, question: "Silicon dioxide has a high melting point because it has:", options: ["A. Ionic bonds", "B. Metallic bonds", "C. Covalent network structure", "D. Weak intermolecular forces"], answer: "C", marks: 1 },
          { id: 9, question: "Which oxide is amphoteric?", options: ["A. Na₂O", "B. MgO", "C. Al₂O₃", "D. SO₂"], answer: "C", marks: 1 },
          { id: 10, question: "Basic oxides are typically formed by:", options: ["A. Non-metals", "B. Metals", "C. Noble gases", "D. Halogens"], answer: "B", marks: 1 },
          { id: 11, question: "The color of Cu²⁺ in aqueous solution is:", options: ["A. Green", "B. Blue", "C. Yellow", "D. Colorless"], answer: "B", marks: 1 },
          { id: 12, question: "Which compound is least soluble in water?", options: ["A. NaCl", "B. AgCl", "C. KNO₃", "D. CaCl₂"], answer: "B", marks: 1 },
          { id: 13, question: "The complex ion [Cu(H₂O)₆]²⁺ has coordination number:", options: ["A. 2", "B. 4", "C. 6", "D. 8"], answer: "C", marks: 1 },
          { id: 14, question: "Electron shielding effect causes:", options: ["A. Increase in effective nuclear charge", "B. Decrease in effective nuclear charge", "C. No change in atomic radius", "D. Increase in ionization energy"], answer: "B", marks: 1 },
          { id: 15, question: "Which has the strongest intermolecular forces?", options: ["A. CH₄", "B. NH₃", "C. H₂O", "D. HF"], answer: "C", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 18,
        questions: [
          { id: 16, question: "Calculate the relative atomic mass of chlorine given: ³⁵Cl (75.77%) and ³⁷Cl (24.23%). Show all working.", marks: 4, answer: "RAM = (35 × 0.7577) + (37 × 0.2423) = 26.52 + 8.97 = 35.49 ≈ 35.5" },
          { id: 17, question: "Explain why the first ionization energy of sodium is lower than that of magnesium.", marks: 4, answer: "Na has 1 electron in outer shell (easier to remove), Mg has 2. Mg has higher nuclear charge with same number of inner shells. Na's outer electron is less tightly held." },
          { id: 18, question: "Write the electronic configuration for: (a) Fe²⁺ (b) Cu⁺ (c) Zn²⁺", marks: 3, answer: "(a) 1s²2s²2p⁶3s²3p⁶3d⁶ (b) 1s²2s²2p⁶3s²3p⁶3d¹⁰ (c) 1s²2s²2p⁶3s²3p⁶3d¹⁰" },
          { id: 19, question: "Describe why sodium oxide is basic while sulfur dioxide is acidic. Include equations.", marks: 4, answer: "Na₂O: Metal oxide, reacts with water to form NaOH (Na₂O + H₂O → 2NaOH). SO₂: Non-metal oxide, reacts with water to form H₂SO₃ (SO₂ + H₂O → H₂SO₃)" },
          { id: 20, question: "Explain hydrogen bonding and give two examples of molecules that exhibit it.", marks: 3, answer: "Hydrogen bonding occurs when H is bonded to highly electronegative atoms (F, O, N). Examples: H₂O (water), NH₃ (ammonia), HF (hydrogen fluoride)" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 12,
        questions: [
          { id: 21, question: "Compare and contrast the properties of ionic compounds and covalent network structures. Include: (a) Bonding present (b) Melting points (c) Electrical conductivity (d) Solubility (e) Examples of each", marks: 12, answer: "Ionic: Electrostatic attraction between ions, high MP, conduct when molten/dissolved, often soluble in water, e.g., NaCl, MgO. Covalent network: Strong covalent bonds throughout structure, very high MP, don't conduct electricity, insoluble, e.g., SiO₂, diamond, graphite (conducts)." }
        ]
      }
    ]
  },
  unit2: {
    id: "y12chem-unit2-test",
    title: "Unit 2: Advanced Stoichiometry & Solutions",
    strand: "Quantitative and Aqueous Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations",
      "Use Avogadro's number: 6.02 × 10²³"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "The limiting reactant in a reaction is:", options: ["A. Always the reactant with smallest mass", "B. The reactant that runs out first", "C. Always present in excess", "D. The product formed"], answer: "B", marks: 1 },
          { id: 2, question: "Percentage yield is calculated as:", options: ["A. (Actual/Theoretical) × 100", "B. (Theoretical/Actual) × 100", "C. Actual - Theoretical", "D. Theoretical - Actual"], answer: "A", marks: 1 },
          { id: 3, question: "A primary standard solution has all EXCEPT:", options: ["A. High purity", "B. Known concentration", "C. Stable in air", "D. Hygroscopic properties"], answer: "D", marks: 1 },
          { id: 4, question: "The concentration of a 0.5 M solution in g/L (molar mass = 40 g/mol) is:", options: ["A. 10 g/L", "B. 20 g/L", "C. 40 g/L", "D. 80 g/L"], answer: "B", marks: 1 },
          { id: 5, question: "At the endpoint of a titration:", options: ["A. Indicator changes color", "B. Reaction is incomplete", "C. No more acid is added", "D. pH is always 7"], answer: "A", marks: 1 },
          { id: 6, question: "According to Bronsted-Lowry theory, an acid is:", options: ["A. A proton acceptor", "B. A proton donor", "C. An electron acceptor", "D. An OH⁻ donor"], answer: "B", marks: 1 },
          { id: 7, question: "The conjugate base of H₂SO₄ is:", options: ["A. H₂SO₃", "B. HSO₄⁻", "C. SO₄²⁻", "D. H₃SO₄⁺"], answer: "B", marks: 1 },
          { id: 8, question: "Water can act as:", options: ["A. Only an acid", "B. Only a base", "C. An amphoteric substance", "D. Neither acid nor base"], answer: "C", marks: 1 },
          { id: 9, question: "The ionic product of water (Kw) at 25°C is:", options: ["A. 1 × 10⁻¹⁴", "B. 1 × 10⁻⁷", "C. 7", "D. 14"], answer: "A", marks: 1 },
          { id: 10, question: "If pH = 3, the [H⁺] is:", options: ["A. 3 M", "B. 10⁻³ M", "C. 10³ M", "D. 0.3 M"], answer: "B", marks: 1 },
          { id: 11, question: "The pOH of a solution with [OH⁻] = 10⁻⁵ M is:", options: ["A. 5", "B. 9", "C. -5", "D. 14"], answer: "A", marks: 1 },
          { id: 12, question: "Phenolphthalein is colorless in:", options: ["A. Acidic solution", "B. Basic solution", "C. Neutral solution only", "D. All solutions"], answer: "A", marks: 1 }
        ]
      },
      {
        name: "Section B: Calculations",
        marks: 24,
        questions: [
          { id: 13, question: "In the reaction 2Al + 3CuSO₄ → Al₂(SO₄)₃ + 3Cu, if 5.4g of Al reacts with 48g of CuSO₄, determine the limiting reactant and mass of Cu produced. (Al=27, Cu=64, S=32, O=16)", marks: 6, answer: "Moles Al = 5.4/27 = 0.2 mol. Moles CuSO₄ = 48/160 = 0.3 mol. From equation: 2 mol Al needs 3 mol CuSO₄. 0.2 mol Al needs 0.3 mol CuSO₄. Both completely used. Mass Cu = 0.3 × 64 = 19.2g" },
          { id: 14, question: "A student prepared 250 mL of NaOH solution using 2.0g of NaOH. Calculate the concentration in: (a) g/L (b) mol/L (Na=23, O=16, H=1)", marks: 4, answer: "(a) Conc = 2.0g/0.25L = 8 g/L (b) Molar mass NaOH = 40 g/mol. Moles = 2.0/40 = 0.05 mol. Conc = 0.05/0.25 = 0.2 M" },
          { id: 15, question: "25.0 mL of HCl was titrated with 0.10 M NaOH. If 22.5 mL of NaOH was required to reach the endpoint, calculate the concentration of HCl.", marks: 4, answer: "Moles NaOH = 0.10 × 0.0225 = 0.00225 mol. HCl:NaOH = 1:1. Moles HCl = 0.00225 mol. [HCl] = 0.00225/0.025 = 0.09 M" },
          { id: 16, question: "Calculate the pH of: (a) 0.01 M HCl (b) 0.001 M NaOH", marks: 4, answer: "(a) [H⁺] = 0.01 M, pH = -log(0.01) = 2 (b) [OH⁻] = 0.001 M, pOH = 3, pH = 14 - 3 = 11" },
          { id: 17, question: "A reaction has theoretical yield of 25g but only 20g was obtained. Calculate the percentage yield.", marks: 2, answer: "% yield = (20/25) × 100 = 80%" },
          { id: 18, question: "Identify the conjugate acid-base pairs in: NH₃ + H₂O ⇌ NH₄⁺ + OH⁻", marks: 4, answer: "Conjugate pair 1: NH₃ (base) / NH₄⁺ (conjugate acid). Conjugate pair 2: H₂O (acid) / OH⁻ (conjugate base)" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 14,
        questions: [
          { id: 19, question: "Describe the procedure for preparing a standard solution of sodium carbonate (Na₂CO₃) and using it to standardize a hydrochloric acid solution. Include: (a) Why Na₂CO₃ is suitable as a primary standard (b) Steps to prepare 250 mL of 0.1 M solution (c) Titration procedure (d) Calculations to find HCl concentration", marks: 14, answer: "Na₂CO₃ is suitable: high purity, stable, non-hygroscopic, known formula. Preparation: Mass = 0.1 × 0.25 × 106 = 2.65g. Weigh accurately, dissolve in distilled water, transfer to volumetric flask, make up to 250 mL. Titration: Pipette 25 mL Na₂CO₃, add indicator (methyl orange), titrate with HCl until color change. Calculation: Na₂CO₃ + 2HCl → 2NaCl + H₂O + CO₂" }
        ]
      }
    ]
  },
  unit3: {
    id: "y12chem-unit3-test",
    title: "Unit 3: Thermochemistry & Kinetics",
    strand: "Physical Chemistry",
    duration: "50 minutes",
    totalMarks: 45,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations",
      "Include units in all answers"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "An exothermic reaction has:", options: ["A. Positive ΔH", "B. Negative ΔH", "C. Zero ΔH", "D. No enthalpy change"], answer: "B", marks: 1 },
          { id: 2, question: "Activation energy is:", options: ["A. Energy released in a reaction", "B. Minimum energy needed to start a reaction", "C. Total energy of products", "D. Energy of reactants"], answer: "B", marks: 1 },
          { id: 3, question: "Hess's Law states that enthalpy change:", options: ["A. Depends on the path taken", "B. Is independent of the path taken", "C. Is always positive", "D. Is always negative"], answer: "B", marks: 1 },
          { id: 4, question: "Which factor does NOT affect reaction rate?", options: ["A. Temperature", "B. Concentration", "C. Catalyst", "D. Enthalpy change"], answer: "D", marks: 1 },
          { id: 5, question: "A catalyst works by:", options: ["A. Increasing activation energy", "B. Providing an alternative pathway with lower Ea", "C. Changing the enthalpy of reaction", "D. Increasing the temperature"], answer: "B", marks: 1 },
          { id: 6, question: "At dynamic equilibrium:", options: ["A. Forward reaction stops", "B. Reverse reaction stops", "C. Rates of forward and reverse reactions are equal", "D. Concentrations are equal"], answer: "C", marks: 1 },
          { id: 7, question: "Le Chatelier's Principle states that a system at equilibrium will:", options: ["A. Stay the same when disturbed", "B. Shift to oppose any change", "C. Always shift to the right", "D. Always shift to the left"], answer: "B", marks: 1 },
          { id: 8, question: "Increasing pressure favors the side with:", options: ["A. More moles of gas", "B. Fewer moles of gas", "C. More mass", "D. Less mass"], answer: "B", marks: 1 },
          { id: 9, question: "The Contact Process is used to make:", options: ["A. Ammonia", "B. Sulfuric acid", "C. Nitric acid", "D. Hydrogen"], answer: "B", marks: 1 },
          { id: 10, question: "The Haber Process equation is:", options: ["A. N₂ + O₂ → 2NO", "B. N₂ + 3H₂ ⇌ 2NH₃", "C. 2SO₂ + O₂ ⇌ 2SO₃", "D. CO + H₂O → CO₂ + H₂"], answer: "B", marks: 1 },
          { id: 11, question: "Increasing temperature in an exothermic reaction:", options: ["A. Shifts equilibrium right", "B. Shifts equilibrium left", "C. No effect on equilibrium", "D. Increases yield"], answer: "B", marks: 1 },
          { id: 12, question: "Bond breaking is:", options: ["A. Exothermic", "B. Endothermic", "C. Neither", "D. Both"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 18,
        questions: [
          { id: 13, question: "Draw and label an energy profile diagram for an exothermic reaction. Include: reactants, products, activation energy, and enthalpy change.", marks: 4, answer: "Diagram showing: Reactants at higher energy level, products at lower level. Activation energy (Ea) as peak from reactants. ΔH shown as negative (downward arrow from reactants to products)." },
          { id: 14, question: "Use Hess's Law to calculate ΔH for: C + O₂ → CO₂, given: C + ½O₂ → CO (ΔH = -110 kJ) and CO + ½O₂ → CO₂ (ΔH = -283 kJ)", marks: 4, answer: "Adding both equations: C + ½O₂ + CO + ½O₂ → CO + CO₂. Simplifies to: C + O₂ → CO₂. ΔH = -110 + (-283) = -393 kJ" },
          { id: 15, question: "Explain how each factor affects reaction rate: (a) Temperature (b) Concentration (c) Surface area", marks: 6, answer: "(a) Higher temp → more kinetic energy → more successful collisions. (b) Higher conc → more particles → more frequent collisions. (c) Larger surface area → more particles exposed → more collisions." },
          { id: 16, question: "For the equilibrium N₂ + 3H₂ ⇌ 2NH₃ (ΔH = -92 kJ), predict the effect of: (a) Increasing pressure (b) Removing NH₃ (c) Increasing temperature", marks: 4, answer: "(a) Shifts right (fewer moles on right). (b) Shifts right (replaces removed NH₃). (c) Shifts left (opposes added heat in exothermic reaction)." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 15,
        questions: [
          { id: 17, question: "Discuss the industrial conditions used in the Haber Process for ammonia production. Include: (a) Equation for the reaction (b) Conditions used (temperature, pressure, catalyst) (c) Explanation of why these conditions are chosen using Le Chatelier's Principle (d) Compromises made between yield and rate", marks: 15, answer: "N₂ + 3H₂ ⇌ 2NH₃ (ΔH = -92 kJ). Conditions: 450°C, 200 atm, iron catalyst. High pressure favors forward (fewer moles). Low temp favors forward (exothermic) but too slow - compromise at 450°C. Catalyst speeds up equilibrium attainment. Compromise: moderate temp gives reasonable rate and yield (~15%)." }
        ]
      }
    ]
  },
  unit4: {
    id: "y12chem-unit4-test",
    title: "Unit 4: Oxidation-Reduction & Electrolysis",
    strand: "Oxidation and Reduction",
    duration: "50 minutes",
    totalMarks: 45,
    instructions: [
      "Answer ALL questions",
      "Show all working",
      "Balance all equations"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "Oxidation is defined as:", options: ["A. Gain of electrons", "B. Loss of electrons", "C. Gain of protons", "D. Loss of protons"], answer: "B", marks: 1 },
          { id: 2, question: "The oxidation state of Mn in KMnO₄ is:", options: ["A. +2", "B. +4", "C. +6", "D. +7"], answer: "D", marks: 1 },
          { id: 3, question: "A reducing agent:", options: ["A. Gains electrons", "B. Loses electrons", "C. Gains protons", "D. Is oxidized itself"], answer: "D", marks: 1 },
          { id: 4, question: "In the reaction Zn + Cu²⁺ → Zn²⁺ + Cu:", options: ["A. Zn is reduced", "B. Cu²⁺ is oxidized", "C. Zn is the reducing agent", "D. Cu²⁺ is the reducing agent"], answer: "C", marks: 1 },
          { id: 5, question: "Electrolysis requires:", options: ["A. An ionic compound in solid state", "B. An ionic compound in molten or aqueous state", "C. A covalent compound", "D. A metal"], answer: "B", marks: 1 },
          { id: 6, question: "At the cathode during electrolysis:", options: ["A. Oxidation occurs", "B. Reduction occurs", "C. No reaction occurs", "D. Anions are attracted"], answer: "B", marks: 1 },
          { id: 7, question: "At the anode during electrolysis:", options: ["A. Cations are reduced", "B. Anions are oxidized", "C. Electrons are gained", "D. Reduction occurs"], answer: "B", marks: 1 },
          { id: 8, question: "The oxidation state of oxygen is usually:", options: ["A. -1", "B. -2", "C. +2", "D. 0"], answer: "B", marks: 1 },
          { id: 9, question: "Corrosion is an example of:", options: ["A. Oxidation only", "B. Reduction only", "C. A redox reaction", "D. Not a redox reaction"], answer: "C", marks: 1 },
          { id: 10, question: "Which is a strong oxidizing agent?", options: ["A. Na", "B. Mg", "C. KMnO₄", "D. Zn"], answer: "C", marks: 1 },
          { id: 11, question: "In an electrolytic cell, energy is:", options: ["A. Released", "B. Supplied externally", "C. Stored", "D. Not involved"], answer: "B", marks: 1 },
          { id: 12, question: "The oxidation state of N in NO₃⁻ is:", options: ["A. +3", "B. +5", "C. -3", "D. 0"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 18,
        questions: [
          { id: 13, question: "Assign oxidation states to all atoms in: (a) H₂SO₄ (b) Na₂Cr₂O₇ (c) NH₄⁺", marks: 6, answer: "(a) H: +1, S: +6, O: -2 (b) Na: +1, Cr: +6, O: -2 (c) N: -3, H: +1" },
          { id: 14, question: "Balance this redox equation using the ion-electron method: Fe²⁺ + MnO₄⁻ → Fe³⁺ + Mn²⁺ (acidic solution)", marks: 6, answer: "Oxidation: Fe²⁺ → Fe³⁺ + e⁻ (×5). Reduction: MnO₄⁻ + 8H⁺ + 5e⁻ → Mn²⁺ + 4H₂O. Overall: 5Fe²⁺ + MnO₄⁻ + 8H⁺ → 5Fe³⁺ + Mn²⁺ + 4H₂O" },
          { id: 15, question: "Draw and label an electrolytic cell for the electrolysis of molten NaCl. Include electrodes, ions, direction of electron flow, and electrode reactions.", marks: 6, answer: "Diagram: Two electrodes in molten NaCl connected to DC power supply. Anode (+): 2Cl⁻ → Cl₂ + 2e⁻. Cathode (-): Na⁺ + e⁻ → Na. Na⁺ moves to cathode, Cl⁻ moves to anode. Electrons flow from anode to cathode through external circuit." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 15,
        questions: [
          { id: 16, question: "Discuss corrosion of iron (rusting). Include: (a) The conditions required for rusting (b) The electrochemical nature of rusting (half-equations) (c) Two methods of preventing rust and explain how each works (d) The environmental and economic impact of corrosion", marks: 15, answer: "Conditions: Water, oxygen, and an electrolyte. Electrochemistry: Anode: Fe → Fe²⁺ + 2e⁻, Cathode: O₂ + 4H⁺ + 4e⁻ → 2H₂O, Fe²⁺ further oxidizes to Fe₂O₃ (rust). Prevention: (1) Galvanizing - Zn coating sacrificially corrodes instead of Fe. (2) Painting - barrier prevents water and oxygen contact. Impact: Structural damage, replacement costs, safety hazards." }
        ]
      }
    ]
  },
  unit5: {
    id: "y12chem-unit5-test",
    title: "Unit 5: Organic Chemistry - Alcohols to Esters",
    strand: "Organic Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    instructions: [
      "Answer ALL questions",
      "Draw clear structural formulas",
      "Use IUPAC nomenclature"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 15,
        questions: [
          { id: 1, question: "The functional group of alcohols is:", options: ["A. -CHO", "B. -OH", "C. -COOH", "D. -CO-"], answer: "B", marks: 1 },
          { id: 2, question: "The general formula for alcohols is:", options: ["A. CₙH₂ₙO", "B. CₙH₂ₙ₊₁OH", "C. CₙH₂ₙ₊₂O", "D. CₙH₂ₙO₂"], answer: "B", marks: 1 },
          { id: 3, question: "Butan-2-ol is a:", options: ["A. Primary alcohol", "B. Secondary alcohol", "C. Tertiary alcohol", "D. Not an alcohol"], answer: "B", marks: 1 },
          { id: 4, question: "Oxidation of a primary alcohol produces:", options: ["A. A ketone", "B. An aldehyde then carboxylic acid", "C. An ester", "D. No reaction"], answer: "B", marks: 1 },
          { id: 5, question: "The functional group -CHO represents:", options: ["A. Ketone", "B. Aldehyde", "C. Carboxylic acid", "D. Alcohol"], answer: "B", marks: 1 },
          { id: 6, question: "Ketones can be distinguished from aldehydes using:", options: ["A. Litmus paper", "B. Fehling's solution", "C. pH indicator", "D. Water"], answer: "B", marks: 1 },
          { id: 7, question: "The IUPAC name for CH₃CH₂CHO is:", options: ["A. Propanal", "B. Propanone", "C. Propanol", "D. Propanoic acid"], answer: "A", marks: 1 },
          { id: 8, question: "Carboxylic acids have the functional group:", options: ["A. -OH", "B. -CHO", "C. -COOH", "D. -COO-"], answer: "C", marks: 1 },
          { id: 9, question: "Carboxylic acids are:", options: ["A. Strong acids", "B. Weak acids", "C. Neutral", "D. Basic"], answer: "B", marks: 1 },
          { id: 10, question: "Esterification is the reaction between:", options: ["A. Two alcohols", "B. Alcohol and carboxylic acid", "C. Two carboxylic acids", "D. Alcohol and water"], answer: "B", marks: 1 },
          { id: 11, question: "The catalyst for esterification is:", options: ["A. Sodium hydroxide", "B. Concentrated sulfuric acid", "C. Water", "D. Heat only"], answer: "B", marks: 1 },
          { id: 12, question: "Esters are characterized by:", options: ["A. Sour taste", "B. Fruity smell", "C. No smell", "D. Bitter taste"], answer: "B", marks: 1 },
          { id: 13, question: "Hydrolysis of fats produces:", options: ["A. Glycerol and fatty acids", "B. Glucose and amino acids", "C. Ethanol and CO₂", "D. Only fatty acids"], answer: "A", marks: 1 },
          { id: 14, question: "Saturated fats have:", options: ["A. C=C double bonds", "B. Only C-C single bonds", "C. Triple bonds", "D. Ring structures"], answer: "B", marks: 1 },
          { id: 15, question: "Saponification produces:", options: ["A. Ester + water", "B. Soap + glycerol", "C. Fat + alcohol", "D. Acid + alcohol"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 20,
        questions: [
          { id: 16, question: "Draw the structural formulas and name all isomers of C₄H₁₀O that are alcohols.", marks: 4, answer: "Butan-1-ol (CH₃CH₂CH₂CH₂OH), Butan-2-ol (CH₃CH(OH)CH₂CH₃), 2-methylpropan-1-ol ((CH₃)₂CHCH₂OH), 2-methylpropan-2-ol ((CH₃)₃COH)" },
          { id: 17, question: "Describe and explain the positive tests for: (a) Aldehydes using Tollens' reagent (b) Aldehydes using Fehling's solution", marks: 4, answer: "(a) Tollens': Ag⁺ reduced to Ag metal (silver mirror). Aldehyde oxidized to carboxylic acid. (b) Fehling's: Cu²⁺ (blue) reduced to Cu₂O (brick-red precipitate). Aldehyde oxidized." },
          { id: 18, question: "Write the equation for esterification of ethanoic acid with propan-1-ol. Name the ester formed.", marks: 4, answer: "CH₃COOH + CH₃CH₂CH₂OH ⇌ CH₃COOCH₂CH₂CH₃ + H₂O. Ester: Propyl ethanoate (or propyl acetate)" },
          { id: 19, question: "Explain why unsaturated fats have lower melting points than saturated fats.", marks: 4, answer: "Unsaturated fats have C=C bonds causing kinks in chains. Molecules can't pack closely together. Weaker intermolecular forces. Less energy needed to separate molecules." },
          { id: 20, question: "Describe the cleaning action of soap at the molecular level.", marks: 4, answer: "Soap has hydrophilic (polar/ionic COO⁻) head and hydrophobic (non-polar hydrocarbon) tail. Hydrophobic tail dissolves in grease/oil, hydrophilic head remains in water. Forms micelles around dirt. Grease suspended in water and washed away." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 15,
        questions: [
          { id: 21, question: "Describe the oxidation reactions of alcohols. Include: (a) Oxidation of primary alcohol (two stages) (b) Oxidation of secondary alcohol (c) Why tertiary alcohols don't oxidize (d) Reagents and conditions used (e) Equations for oxidation of propan-1-ol and propan-2-ol", marks: 15, answer: "Primary: Aldehyde then carboxylic acid (CH₃CH₂CH₂OH → CH₃CH₂CHO → CH₃CH₂COOH). Secondary: Ketone only (CH₃CH(OH)CH₃ → CH₃COCH₃). Tertiary: No H on C with OH, cannot be oxidized. Reagents: acidified potassium dichromate (K₂Cr₂O₇/H₂SO₄), heat. Orange → green color change indicates oxidation." }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 13 CHEMISTRY UNIT TESTS
// ============================================
export const year13ChemistryUnitTests = {
  unit1: {
    id: "y13chem-unit1-test",
    title: "Unit 1: Quantum Chemistry & Molecular Structure",
    strand: "Atomic Structure, Bonding and Related Properties",
    duration: "55 minutes",
    totalMarks: 50,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations",
      "Use the Periodic Table and data sheet provided"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 15,
        questions: [
          { id: 1, question: "The Bohr model of the atom correctly explains:", options: ["A. Spectra of all atoms", "B. Line spectrum of hydrogen", "C. Bonding in molecules", "D. Radioactive decay"], answer: "B", marks: 1 },
          { id: 2, question: "Emission spectra are produced when electrons:", options: ["A. Are absorbed by atoms", "B. Move to higher energy levels", "C. Fall to lower energy levels", "D. Leave the atom"], answer: "C", marks: 1 },
          { id: 3, question: "The principal quantum number (n) determines:", options: ["A. Shape of orbital", "B. Energy level/shell", "C. Orbital orientation", "D. Electron spin"], answer: "B", marks: 1 },
          { id: 4, question: "The azimuthal quantum number (l) determines:", options: ["A. Shell number", "B. Orbital shape (s, p, d, f)", "C. Spin direction", "D. Number of electrons"], answer: "B", marks: 1 },
          { id: 5, question: "Electron affinity is:", options: ["A. Energy needed to remove an electron", "B. Energy released when gaining an electron", "C. Energy of formation", "D. Bond energy"], answer: "B", marks: 1 },
          { id: 6, question: "VSEPR theory predicts molecular shapes based on:", options: ["A. Electron masses", "B. Nuclear charges", "C. Electron pair repulsion", "D. Bond energies"], answer: "C", marks: 1 },
          { id: 7, question: "A molecule with 4 bonding pairs and 0 lone pairs has shape:", options: ["A. Linear", "B. Trigonal planar", "C. Tetrahedral", "D. Octahedral"], answer: "C", marks: 1 },
          { id: 8, question: "NH₃ has a _____ shape due to 3 bonding pairs and 1 lone pair:", options: ["A. Linear", "B. Trigonal planar", "C. Tetrahedral", "D. Trigonal pyramidal"], answer: "D", marks: 1 },
          { id: 9, question: "A polar molecule must have:", options: ["A. Only polar bonds", "B. Non-symmetric distribution of charge", "C. Ionic bonds", "D. No lone pairs"], answer: "B", marks: 1 },
          { id: 10, question: "CO₂ is non-polar because:", options: ["A. C-O bonds are non-polar", "B. The molecule is linear and symmetric", "C. It has no lone pairs", "D. Carbon is non-polar"], answer: "B", marks: 1 },
          { id: 11, question: "Alpha particles are:", options: ["A. Electrons", "B. Helium nuclei", "C. Protons", "D. Neutrons"], answer: "B", marks: 1 },
          { id: 12, question: "Beta decay involves:", options: ["A. Loss of helium nucleus", "B. Conversion of neutron to proton with electron emission", "C. Emission of gamma rays", "D. Loss of protons"], answer: "B", marks: 1 },
          { id: 13, question: "Half-life is the time for:", options: ["A. All nuclei to decay", "B. Half the nuclei to decay", "C. One nucleus to decay", "D. Radiation to stop"], answer: "B", marks: 1 },
          { id: 14, question: "Transition metals are characterized by:", options: ["A. Complete d orbitals", "B. Incomplete d orbitals", "C. No d orbitals", "D. Complete p orbitals"], answer: "B", marks: 1 },
          { id: 15, question: "The electronic configuration of Fe²⁺ is:", options: ["A. [Ar]3d⁶", "B. [Ar]3d⁴4s²", "C. [Ar]3d⁵4s¹", "D. [Ar]3d⁸"], answer: "A", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 20,
        questions: [
          { id: 16, question: "Explain how the line spectrum of hydrogen provides evidence for quantized energy levels in atoms.", marks: 4, answer: "Discrete lines (not continuous) show electrons can only exist at specific energy levels. Each line corresponds to a specific wavelength/energy. Electron transitions between fixed levels produce specific photon energies." },
          { id: 17, question: "Using VSEPR theory, predict and explain the shapes of: (a) BF₃ (b) H₂O (c) SF₆", marks: 6, answer: "(a) BF₃: 3 bonding pairs, 0 lone pairs → trigonal planar, 120°. (b) H₂O: 2 bonding pairs, 2 lone pairs → bent/angular, 104.5°. (c) SF₆: 6 bonding pairs, 0 lone pairs → octahedral, 90°." },
          { id: 18, question: "A radioactive isotope has a half-life of 8 days. If you start with 100g, how much remains after: (a) 16 days (b) 24 days? Show working.", marks: 4, answer: "(a) 16 days = 2 half-lives. 100 → 50 → 25g. (b) 24 days = 3 half-lives. 100 → 50 → 25 → 12.5g" },
          { id: 19, question: "Explain why transition metals often form colored compounds and have variable oxidation states.", marks: 4, answer: "Colored: Partially filled d orbitals absorb visible light for d-d transitions. Transmitted light appears colored (complementary color). Variable oxidation states: Similar energies of 3d and 4s orbitals allow loss of different numbers of electrons." },
          { id: 20, question: "Explain polarity in terms of electronegativity and molecular geometry. Why is CHCl₃ polar but CCl₄ non-polar?", marks: 2, answer: "CHCl₃: Tetrahedral but not symmetric (C-H vs C-Cl), dipoles don't cancel → polar. CCl₄: Tetrahedral and symmetric, all C-Cl bonds, dipoles cancel → non-polar." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 15,
        questions: [
          { id: 21, question: "Discuss nuclear chemistry. Include: (a) Definitions of fission and fusion with examples (b) Types of radioactive decay (α, β, γ) with equations (c) Applications of radioactivity in medicine and industry (d) Health hazards and safety precautions", marks: 15, answer: "Fission: Splitting heavy nuclei (²³⁵U → smaller nuclei + neutrons + energy). Fusion: Combining light nuclei (²H + ³H → ⁴He + n + energy). Alpha: ²³⁸U → ²³⁴Th + ⁴He. Beta: ¹⁴C → ¹⁴N + e⁻. Gamma: High energy photons after α/β decay. Applications: Medical imaging (tracers), cancer treatment, sterilization, smoke detectors. Hazards: Ionizing radiation damages cells/DNA. Safety: Shielding, distance, time limits, monitoring." }
        ]
      }
    ]
  },
  unit2: {
    id: "y13chem-unit2-test",
    title: "Unit 2: Acid-Base Equilibria & Buffers",
    strand: "Quantitative and Aqueous Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations",
      "Use Ka and Kb values provided where needed"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "Ka is the:", options: ["A. Base dissociation constant", "B. Acid dissociation constant", "C. Equilibrium constant", "D. Ionic product"], answer: "B", marks: 1 },
          { id: 2, question: "A large Ka value indicates:", options: ["A. A weak acid", "B. A strong acid", "C. A weak base", "D. A strong base"], answer: "B", marks: 1 },
          { id: 3, question: "For a weak acid HA: Ka = ", options: ["A. [HA]/[H⁺][A⁻]", "B. [H⁺][A⁻]/[HA]", "C. [H⁺][HA]/[A⁻]", "D. [A⁻]/[H⁺][HA]"], answer: "B", marks: 1 },
          { id: 4, question: "If Ka = 1.8 × 10⁻⁵, pKa is approximately:", options: ["A. 1.8", "B. 4.7", "C. 5", "D. 9.3"], answer: "B", marks: 1 },
          { id: 5, question: "A buffer solution:", options: ["A. Has pH = 7 always", "B. Resists pH changes", "C. Is always acidic", "D. Contains only acids"], answer: "B", marks: 1 },
          { id: 6, question: "An acidic buffer is made from:", options: ["A. Strong acid + strong base", "B. Weak acid + conjugate base", "C. Strong acid + weak base", "D. Two strong acids"], answer: "B", marks: 1 },
          { id: 7, question: "The Henderson-Hasselbalch equation is:", options: ["A. pH = pKa + log([HA]/[A⁻])", "B. pH = pKa + log([A⁻]/[HA])", "C. pH = Ka + [A⁻]/[HA]", "D. pH = pKa - log([A⁻]/[HA])"], answer: "B", marks: 1 },
          { id: 8, question: "At the equivalence point of a strong acid-strong base titration:", options: ["A. pH = 7", "B. pH < 7", "C. pH > 7", "D. pH is undefined"], answer: "A", marks: 1 },
          { id: 9, question: "At the equivalence point of weak acid-strong base titration:", options: ["A. pH = 7", "B. pH < 7", "C. pH > 7", "D. No buffer region"], answer: "C", marks: 1 },
          { id: 10, question: "The buffer region on a titration curve is where:", options: ["A. pH changes rapidly", "B. pH changes slowly", "C. Equivalence point occurs", "D. Reaction is complete"], answer: "B", marks: 1 },
          { id: 11, question: "Phenolphthalein is suitable for titrations with:", options: ["A. Equivalence point at pH 4", "B. Equivalence point at pH 7", "C. Equivalence point at pH 9", "D. All titrations"], answer: "C", marks: 1 },
          { id: 12, question: "Ka × Kb equals:", options: ["A. 1", "B. 14", "C. Kw", "D. pH + pOH"], answer: "C", marks: 1 }
        ]
      },
      {
        name: "Section B: Calculations",
        marks: 24,
        questions: [
          { id: 13, question: "Calculate the pH of 0.1 M ethanoic acid (Ka = 1.8 × 10⁻⁵). Show all working.", marks: 6, answer: "Ka = [H⁺][A⁻]/[HA]. Let [H⁺] = x. 1.8 × 10⁻⁵ = x²/0.1. x² = 1.8 × 10⁻⁶. x = 1.34 × 10⁻³ M. pH = -log(1.34 × 10⁻³) = 2.87" },
          { id: 14, question: "A buffer is made from 0.2 M CH₃COOH and 0.3 M CH₃COONa. Calculate the pH. (Ka = 1.8 × 10⁻⁵)", marks: 5, answer: "pH = pKa + log([A⁻]/[HA]). pKa = -log(1.8 × 10⁻⁵) = 4.74. pH = 4.74 + log(0.3/0.2) = 4.74 + 0.18 = 4.92" },
          { id: 15, question: "Calculate the pH when 10 mL of 0.1 M NaOH is added to the buffer in Q14 (assume 100 mL total buffer).", marks: 5, answer: "Moles NaOH = 0.001 mol. This converts 0.001 mol HA to A⁻. New [HA] = (0.02 - 0.001)/0.11 = 0.173 M. New [A⁻] = (0.03 + 0.001)/0.11 = 0.282 M. pH = 4.74 + log(0.282/0.173) = 4.74 + 0.21 = 4.95" },
          { id: 16, question: "Sketch and label a titration curve for the titration of a weak acid with a strong base. Indicate: initial pH, buffer region, equivalence point, final pH.", marks: 4, answer: "Curve starts at low pH (2-4), gradual rise (buffer region), steep rise at equivalence point (pH > 7), levels off at high pH. Label half-equivalence point where pH = pKa." },
          { id: 17, question: "Calculate Kb for the conjugate base of an acid with Ka = 4.0 × 10⁻⁵.", marks: 4, answer: "Ka × Kb = Kw = 1 × 10⁻¹⁴. Kb = 1 × 10⁻¹⁴ / 4.0 × 10⁻⁵ = 2.5 × 10⁻¹⁰" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 14,
        questions: [
          { id: 18, question: "Discuss buffer solutions in detail. Include: (a) Definition and components of acidic and basic buffers (b) How buffers resist pH change when acid or base is added (equations) (c) The Henderson-Hasselbalch equation and its use (d) Importance of buffers in biological systems (blood pH)", marks: 14, answer: "Acidic buffer: weak acid + conjugate base (e.g., CH₃COOH/CH₃COO⁻). Basic buffer: weak base + conjugate acid. Resistance: Added H⁺ reacts with A⁻ → HA. Added OH⁻ reacts with HA → A⁻ + H₂O. Henderson-Hasselbalch: pH = pKa + log([A⁻]/[HA]). Blood: carbonic acid/bicarbonate buffer (H₂CO₃/HCO₃⁻) maintains pH 7.35-7.45. Critical for enzyme function." }
        ]
      }
    ]
  },
  unit3: {
    id: "y13chem-unit3-test",
    title: "Unit 3: Thermodynamics & Equilibrium Constants",
    strand: "Physical Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    instructions: [
      "Answer ALL questions",
      "Show all working for calculations",
      "Include units in all answers"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "Bond energy is the energy required to:", options: ["A. Form a bond", "B. Break a bond", "C. Make a molecule", "D. Change state"], answer: "B", marks: 1 },
          { id: 2, question: "Standard enthalpy of formation is measured at:", options: ["A. 0°C, 1 atm", "B. 25°C, 1 atm", "C. 100°C, 1 atm", "D. Any temperature"], answer: "B", marks: 1 },
          { id: 3, question: "The enthalpy of formation of an element in its standard state is:", options: ["A. Positive", "B. Negative", "C. Zero", "D. Cannot be determined"], answer: "C", marks: 1 },
          { id: 4, question: "The First Law of Thermodynamics states:", options: ["A. Energy is created in reactions", "B. Energy cannot be created or destroyed", "C. Entropy always increases", "D. Heat flows from cold to hot"], answer: "B", marks: 1 },
          { id: 5, question: "Specific heat capacity is energy needed to:", options: ["A. Melt 1 kg of substance", "B. Raise 1 g by 1°C", "C. Vaporize 1 mol", "D. Break one bond"], answer: "B", marks: 1 },
          { id: 6, question: "The equilibrium constant Kc:", options: ["A. Changes with concentration", "B. Changes with temperature only", "C. Never changes", "D. Changes with pressure"], answer: "B", marks: 1 },
          { id: 7, question: "If Qc < Kc, the reaction:", options: ["A. Is at equilibrium", "B. Will shift right", "C. Will shift left", "D. Has stopped"], answer: "B", marks: 1 },
          { id: 8, question: "A large Kc value means:", options: ["A. Products are favored", "B. Reactants are favored", "C. Equilibrium is not reached", "D. No reaction occurs"], answer: "A", marks: 1 },
          { id: 9, question: "Ksp is the:", options: ["A. Acid dissociation constant", "B. Base dissociation constant", "C. Solubility product", "D. Rate constant"], answer: "C", marks: 1 },
          { id: 10, question: "The common ion effect:", options: ["A. Increases solubility", "B. Decreases solubility", "C. Has no effect on solubility", "D. Only applies to acids"], answer: "B", marks: 1 },
          { id: 11, question: "If Ksp of AgCl = 1.8 × 10⁻¹⁰, what is [Ag⁺] in saturated solution?", options: ["A. 1.8 × 10⁻¹⁰ M", "B. 1.34 × 10⁻⁵ M", "C. 9 × 10⁻⁶ M", "D. 1.8 × 10⁻⁵ M"], answer: "B", marks: 1 },
          { id: 12, question: "ΔH can be calculated from bond energies using:", options: ["A. Bonds broken - bonds formed", "B. Bonds formed - bonds broken", "C. Bonds broken + bonds formed", "D. Bonds broken × bonds formed"], answer: "A", marks: 1 }
        ]
      },
      {
        name: "Section B: Calculations",
        marks: 24,
        questions: [
          { id: 13, question: "Calculate ΔH for CH₄ + 2O₂ → CO₂ + 2H₂O using bond energies: C-H = 413 kJ/mol, O=O = 498 kJ/mol, C=O = 805 kJ/mol, O-H = 463 kJ/mol", marks: 6, answer: "Bonds broken: 4(C-H) + 2(O=O) = 4(413) + 2(498) = 1652 + 996 = 2648 kJ. Bonds formed: 2(C=O) + 4(O-H) = 2(805) + 4(463) = 1610 + 1852 = 3462 kJ. ΔH = 2648 - 3462 = -814 kJ/mol" },
          { id: 14, question: "50g of water was heated from 20°C to 80°C. Calculate the heat energy absorbed. (c = 4.18 J/g°C)", marks: 4, answer: "q = mcΔT = 50 × 4.18 × 60 = 12,540 J = 12.54 kJ" },
          { id: 15, question: "For the reaction N₂ + 3H₂ ⇌ 2NH₃, if at equilibrium [N₂] = 0.5 M, [H₂] = 1.5 M, [NH₃] = 0.8 M, calculate Kc.", marks: 5, answer: "Kc = [NH₃]²/([N₂][H₂]³) = (0.8)²/(0.5 × 1.5³) = 0.64/(0.5 × 3.375) = 0.64/1.6875 = 0.38" },
          { id: 16, question: "Calculate the solubility of CaF₂ in g/L. (Ksp = 4.0 × 10⁻¹¹, Ca = 40, F = 19)", marks: 5, answer: "CaF₂ ⇌ Ca²⁺ + 2F⁻. Ksp = [Ca²⁺][F⁻]² = s(2s)² = 4s³. 4s³ = 4.0 × 10⁻¹¹. s³ = 1.0 × 10⁻¹¹. s = 2.15 × 10⁻⁴ M. Solubility = 2.15 × 10⁻⁴ × 78 = 0.017 g/L" },
          { id: 17, question: "Predict if a precipitate forms when 50 mL of 0.001 M AgNO₃ is mixed with 50 mL of 0.002 M NaCl. (Ksp AgCl = 1.8 × 10⁻¹⁰)", marks: 4, answer: "After mixing: [Ag⁺] = 0.001 × 50/100 = 5 × 10⁻⁴ M. [Cl⁻] = 0.002 × 50/100 = 1 × 10⁻³ M. Qsp = [Ag⁺][Cl⁻] = 5 × 10⁻⁴ × 1 × 10⁻³ = 5 × 10⁻⁷. Qsp > Ksp, so precipitate forms." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 14,
        questions: [
          { id: 18, question: "Discuss the relationship between reaction quotient (Qc) and equilibrium constant (Kc). Include: (a) Definitions of Qc and Kc (b) How to determine reaction direction from Qc and Kc (c) Worked example with calculations (d) Real-life application (e.g., industrial process)", marks: 14, answer: "Kc: Ratio of products to reactants at equilibrium (constant at given T). Qc: Same ratio at any point (not necessarily equilibrium). If Qc < Kc: Forward reaction favored, shifts right. If Qc > Kc: Reverse reaction favored, shifts left. If Qc = Kc: At equilibrium. Application: Haber process - monitoring Qc helps optimize NH₃ production by adjusting conditions when system deviates from optimal Kc." }
        ]
      }
    ]
  },
  unit4: {
    id: "y13chem-unit4-test",
    title: "Unit 4: Electrochemistry & Galvanic Cells",
    strand: "Oxidation and Reduction",
    duration: "55 minutes",
    totalMarks: 50,
    instructions: [
      "Answer ALL questions",
      "Show all working",
      "Use standard electrode potentials provided"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "In a galvanic cell, the anode is:", options: ["A. Positive electrode", "B. Negative electrode", "C. Where reduction occurs", "D. Made of platinum"], answer: "B", marks: 1 },
          { id: 2, question: "The salt bridge in a galvanic cell:", options: ["A. Provides electrons", "B. Maintains electrical neutrality", "C. Increases voltage", "D. Speeds up the reaction"], answer: "B", marks: 1 },
          { id: 3, question: "E°cell is calculated as:", options: ["A. E°cathode + E°anode", "B. E°cathode - E°anode", "C. E°anode - E°cathode", "D. E°cathode × E°anode"], answer: "B", marks: 1 },
          { id: 4, question: "A positive E°cell value indicates:", options: ["A. Non-spontaneous reaction", "B. Spontaneous reaction", "C. No reaction", "D. Equilibrium"], answer: "B", marks: 1 },
          { id: 5, question: "The standard hydrogen electrode has E° of:", options: ["A. +1.00 V", "B. -1.00 V", "C. 0.00 V", "D. +0.34 V"], answer: "C", marks: 1 },
          { id: 6, question: "In IUPAC cell notation, || represents:", options: ["A. The cathode", "B. The anode", "C. The salt bridge", "D. Electrons"], answer: "C", marks: 1 },
          { id: 7, question: "A species with more positive E° is:", options: ["A. Better reducing agent", "B. Better oxidizing agent", "C. More likely to be oxidized", "D. Non-reactive"], answer: "B", marks: 1 },
          { id: 8, question: "Redox titrations use:", options: ["A. Acid-base indicators", "B. Redox indicators", "C. pH meters only", "D. Thermometers"], answer: "B", marks: 1 },
          { id: 9, question: "The difference between galvanic and electrolytic cells is:", options: ["A. Galvanic cells use external power", "B. Electrolytic cells are spontaneous", "C. Galvanic cells are spontaneous, electrolytic need external power", "D. No difference"], answer: "C", marks: 1 },
          { id: 10, question: "In an electrolytic cell, the cathode is:", options: ["A. Positive", "B. Negative", "C. Neutral", "D. Not used"], answer: "B", marks: 1 },
          { id: 11, question: "Faraday's constant is approximately:", options: ["A. 96,500 C/mol", "B. 9,650 C/mol", "C. 6.02 × 10²³", "D. 22.4 L/mol"], answer: "A", marks: 1 },
          { id: 12, question: "Given: Zn²⁺/Zn E° = -0.76V, Cu²⁺/Cu E° = +0.34V. In a Zn-Cu cell:", options: ["A. Zn is cathode", "B. Cu is anode", "C. Zn is anode", "D. Both are anodes"], answer: "C", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 24,
        questions: [
          { id: 13, question: "Draw and label a galvanic cell using Zn and Cu electrodes. Include: electrodes, electrolytes, salt bridge, direction of electron flow, and half-equations.", marks: 6, answer: "Diagram: Zn electrode in ZnSO₄ (anode), Cu electrode in CuSO₄ (cathode), connected by wire and salt bridge. Anode: Zn → Zn²⁺ + 2e⁻. Cathode: Cu²⁺ + 2e⁻ → Cu. Electrons flow from Zn to Cu through wire." },
          { id: 14, question: "Calculate E°cell for: Mg | Mg²⁺ || Ag⁺ | Ag. Given: E°(Mg²⁺/Mg) = -2.37V, E°(Ag⁺/Ag) = +0.80V. Is the reaction spontaneous?", marks: 4, answer: "E°cell = E°cathode - E°anode = +0.80 - (-2.37) = +3.17V. Positive E°cell, so spontaneous." },
          { id: 15, question: "Write IUPAC cell notation for a cell with: Zn electrode in 1M Zn(NO₃)₂ and Cu electrode in 1M Cu(NO₃)₂.", marks: 3, answer: "Zn(s) | Zn²⁺(aq, 1M) || Cu²⁺(aq, 1M) | Cu(s)" },
          { id: 16, question: "Explain why metals higher in the activity series make better reducing agents.", marks: 4, answer: "Metals higher in activity series have more negative E° values. They lose electrons more easily (oxidized). When they lose electrons, they reduce other species. Therefore better reducing agents." },
          { id: 17, question: "Describe the difference between a galvanic cell and an electrolytic cell in terms of: (a) Energy conversion (b) Spontaneity (c) Electrode signs", marks: 4, answer: "(a) Galvanic: Chemical → electrical. Electrolytic: Electrical → chemical. (b) Galvanic: Spontaneous. Electrolytic: Non-spontaneous. (c) Galvanic: Anode negative, cathode positive. Electrolytic: Anode positive, cathode negative." },
          { id: 18, question: "Describe a redox titration to determine the concentration of Fe²⁺ using KMnO₄.", marks: 3, answer: "Pipette Fe²⁺ solution into flask, add dilute H₂SO₄. Titrate with standardized KMnO₄ from burette. Endpoint: Purple KMnO₄ persists (MnO₄⁻ + 8H⁺ + 5Fe²⁺ → Mn²⁺ + 5Fe³⁺ + 4H₂O). Calculate [Fe²⁺] using mole ratios." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 14,
        questions: [
          { id: 19, question: "Discuss electrochemical cells and their applications. Include: (a) Comparison of galvanic and electrolytic cells (b) Calculation of E°cell and spontaneity (c) Applications of galvanic cells (batteries) (d) Applications of electrolytic cells (electroplating, extraction)", marks: 14, answer: "Galvanic: Spontaneous redox produces electricity (batteries, fuel cells). Positive E°cell = spontaneous. Electrolytic: External electricity drives non-spontaneous reactions. Applications: Batteries (car, phone), electroplating (chrome, silver), aluminum extraction (Hall-Héroult), chlor-alkali process (NaOH, Cl₂)." }
        ]
      }
    ]
  },
  unit5: {
    id: "y13chem-unit5-test",
    title: "Unit 5: Advanced Organic Chemistry",
    strand: "Organic Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    instructions: [
      "Answer ALL questions",
      "Draw clear structural formulas",
      "Use IUPAC nomenclature"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 15,
        questions: [
          { id: 1, question: "The functional group of amines is:", options: ["A. -COOH", "B. -NH₂", "C. -CHO", "D. -OH"], answer: "B", marks: 1 },
          { id: 2, question: "Primary amines have:", options: ["A. 3 alkyl groups on N", "B. 2 alkyl groups on N", "C. 1 alkyl group on N", "D. No alkyl groups on N"], answer: "C", marks: 1 },
          { id: 3, question: "Amides are formed from:", options: ["A. Amine + alcohol", "B. Amine + carboxylic acid", "C. Alcohol + acid", "D. Two amines"], answer: "B", marks: 1 },
          { id: 4, question: "Substitution reactions involve:", options: ["A. Addition of atoms", "B. Removal of atoms", "C. Replacement of atoms/groups", "D. Rearrangement only"], answer: "C", marks: 1 },
          { id: 5, question: "Elimination reactions typically produce:", options: ["A. Saturated compounds", "B. Unsaturated compounds", "C. Cyclic compounds", "D. Ionic compounds"], answer: "B", marks: 1 },
          { id: 6, question: "Hydrolysis of esters produces:", options: ["A. Alcohol + carboxylic acid", "B. Amine + acid", "C. Aldehyde + ketone", "D. Two alcohols"], answer: "A", marks: 1 },
          { id: 7, question: "Acyl chlorides have the functional group:", options: ["A. -COOH", "B. -COCl", "C. -CHO", "D. -CONH₂"], answer: "B", marks: 1 },
          { id: 8, question: "Acyl chlorides react with alcohols to form:", options: ["A. Amides", "B. Esters", "C. Carboxylic acids", "D. Ketones"], answer: "B", marks: 1 },
          { id: 9, question: "Triglycerides are esters of:", options: ["A. Methanol and fatty acids", "B. Glycerol and fatty acids", "C. Ethanol and fatty acids", "D. Glucose and fatty acids"], answer: "B", marks: 1 },
          { id: 10, question: "Monosaccharides have the general formula:", options: ["A. CₙH₂ₙOₙ", "B. (C₆H₁₀O₅)ₙ", "C. CₙH₂ₙ₊₂Oₙ", "D. Cₙ(H₂O)ₙ"], answer: "D", marks: 1 },
          { id: 11, question: "Glucose is a:", options: ["A. Disaccharide", "B. Monosaccharide", "C. Polysaccharide", "D. Lipid"], answer: "B", marks: 1 },
          { id: 12, question: "Starch and cellulose are both polymers of:", options: ["A. Fructose", "B. Sucrose", "C. Glucose", "D. Maltose"], answer: "C", marks: 1 },
          { id: 13, question: "Condensation polymerization releases:", options: ["A. Hydrogen", "B. Oxygen", "C. Water", "D. Carbon dioxide"], answer: "C", marks: 1 },
          { id: 14, question: "Benedict's test is positive for:", options: ["A. All sugars", "B. Reducing sugars only", "C. Non-reducing sugars", "D. Proteins"], answer: "B", marks: 1 },
          { id: 15, question: "The iodine test gives blue-black color with:", options: ["A. Glucose", "B. Sucrose", "C. Starch", "D. Cellulose"], answer: "C", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 20,
        questions: [
          { id: 16, question: "Draw structural formulas for: (a) Propylamine (b) N-methylethanamine (c) Ethanamide", marks: 3, answer: "(a) CH₃CH₂CH₂NH₂ (b) CH₃NHCH₂CH₃ (c) CH₃CONH₂" },
          { id: 17, question: "Write equations for: (a) Acid hydrolysis of ethyl ethanoate (b) Alkaline hydrolysis of ethyl ethanoate", marks: 4, answer: "(a) CH₃COOCH₂CH₃ + H₂O ⇌ CH₃COOH + CH₃CH₂OH (acid catalyst). (b) CH₃COOCH₂CH₃ + NaOH → CH₃COONa + CH₃CH₂OH (irreversible)" },
          { id: 18, question: "Explain the reactions of ethanoyl chloride with: (a) Water (b) Ethanol (c) Ammonia. Include equations.", marks: 6, answer: "(a) CH₃COCl + H₂O → CH₃COOH + HCl. (b) CH₃COCl + C₂H₅OH → CH₃COOC₂H₅ + HCl. (c) CH₃COCl + 2NH₃ → CH₃CONH₂ + NH₄Cl" },
          { id: 19, question: "Describe the structure of a triglyceride. Draw a simplified structure showing glycerol linked to three fatty acid chains.", marks: 4, answer: "Glycerol backbone (3 -OH groups) esterified to 3 fatty acid chains. Structure: CH₂-O-CO-R₁, CH-O-CO-R₂, CH₂-O-CO-R₃ where R = hydrocarbon chains of fatty acids" },
          { id: 20, question: "Compare the structures of starch and cellulose. Why can humans digest starch but not cellulose?", marks: 3, answer: "Both are glucose polymers. Starch: α-glucose, α-1,4 linkages. Cellulose: β-glucose, β-1,4 linkages. Humans have enzymes (amylase) to break α-linkages but not β-linkages." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 15,
        questions: [
          { id: 21, question: "Discuss carbohydrates in detail. Include: (a) Classification (mono-, di-, polysaccharides) with examples (b) Structure of glucose (open chain and ring form) (c) Formation of disaccharides and polysaccharides (glycosidic bonds) (d) Tests for reducing and non-reducing sugars (e) Biological importance of carbohydrates", marks: 15, answer: "Monosaccharides: Glucose, fructose (C₆H₁₂O₆). Disaccharides: Sucrose, maltose, lactose (C₁₂H₂₂O₁₁). Polysaccharides: Starch, cellulose, glycogen (C₆H₁₀O₅)ₙ. Glucose: Open chain with aldehyde group, ring form (α or β). Glycosidic bond: Condensation between -OH groups. Tests: Benedict's/Fehling's for reducing sugars (glucose, maltose). Non-reducing (sucrose) needs hydrolysis first. Importance: Energy source, structural material, energy storage." }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 12 & 13 CHEMISTRY EXERCISES
// ============================================
export const year12ChemistryExercises = {
  unit1: {
    unitTitle: "Ionic Compounds & Periodic Trends",
    exercises: [
      { 
        id: "y12chem-ex1-1", 
        title: "Ionic Compounds Practice", 
        type: "Worksheet",
        instructions: "Complete the following exercises on ionic compounds and electronic configurations.",
        tasks: [
          "Write the ionic formulas for: sodium chloride, magnesium oxide, aluminium sulfate, calcium nitrate, iron(III) chloride",
          "Draw the electronic configuration of Na⁺, Mg²⁺, O²⁻, Cl⁻",
          "Explain why ionic compounds have high melting points",
          "Calculate the percentage composition of each element in CaCO₃",
          "Predict the ionic formula when Group 2 metals react with Group 7 non-metals"
        ],
        materials: "Periodic table, calculator"
      },
      { 
        id: "y12chem-ex1-2", 
        title: "Periodic Trends Worksheet", 
        type: "Analysis",
        instructions: "Analyse periodic trends and answer the following questions.",
        tasks: [
          "Explain the trend in ionization energy across Period 3",
          "Compare atomic and ionic radii of Na, Mg, Al and their ions",
          "Rank F, Cl, Br, I in order of electronegativity",
          "Explain why electron affinity becomes less negative down a group",
          "Describe the shielding effect and its influence on atomic radius"
        ],
        materials: "Periodic table with data"
      },
      { 
        id: "y12chem-ex1-3", 
        title: "Intermolecular Forces", 
        type: "Practical",
        instructions: "Identify and compare intermolecular forces in various molecules.",
        tasks: [
          "Identify the types of intermolecular forces in: H₂O, CH₄, HCl, NH₃",
          "Explain why H₂O has a higher boiling point than H₂S",
          "Compare the strength of London dispersion forces in CH₄ vs C₄H₁₀",
          "Draw diagrams showing hydrogen bonding between water molecules",
          "Predict which compound has higher boiling point: CH₃OH or CH₃CH₃"
        ],
        materials: "Molecular models"
      }
    ]
  },
  unit2: {
    unitTitle: "Advanced Stoichiometry & Solutions",
    exercises: [
      { 
        id: "y12chem-ex2-1", 
        title: "Limiting Reactants", 
        type: "Calculations",
        instructions: "Solve stoichiometry problems involving limiting reactants.",
        tasks: [
          "In 2Al + 3CuSO₄ → Al₂(SO₄)₃ + 3Cu, find limiting reactant when 5g Al reacts with 20g CuSO₄",
          "Calculate the mass of product when 10g of limiting reactant is used",
          "Determine percentage yield when actual yield is 15g and theoretical yield is 18g",
          "Explain why reactions rarely give 100% yield",
          "Calculate excess reactant remaining after reaction"
        ],
        materials: "Calculator, periodic table"
      },
      { 
        id: "y12chem-ex2-2", 
        title: "Titration Calculations", 
        type: "Calculations",
        instructions: "Complete titration calculations and analysis.",
        tasks: [
          "Calculate the concentration of HCl if 25mL requires 22mL of 0.1M NaOH",
          "Determine the mass of Na₂CO₃ needed for 250mL of 0.05M solution",
          "Explain the purpose of a primary standard in titration",
          "Calculate [H₂SO₄] from titration with standardized NaOH",
          "Describe the procedure for preparing a standard solution"
        ],
        materials: "Calculator"
      },
      { 
        id: "y12chem-ex2-3", 
        title: "pH Calculations", 
        type: "Calculations",
        instructions: "Calculate pH, pOH, and ion concentrations.",
        tasks: [
          "Calculate pH of 0.01M HCl and 0.001M NaOH",
          "Determine [H⁺] when pH = 4.5",
          "Calculate pOH when [OH⁻] = 2.5 × 10⁻⁴ M",
          "Verify that pH + pOH = 14 at 25°C",
          "Identify conjugate acid-base pairs in: HCO₃⁻ + H₂O ⇌ H₂CO₃ + OH⁻"
        ],
        materials: "Calculator, log tables"
      }
    ]
  },
  unit3: {
    unitTitle: "Thermochemistry & Kinetics",
    exercises: [
      { 
        id: "y12chem-ex3-1", 
        title: "Hess's Law Problems", 
        type: "Calculations",
        instructions: "Apply Hess's Law to calculate enthalpy changes.",
        tasks: [
          "Calculate ΔH for C + ½O₂ → CO using given combustion data",
          "Use formation enthalpies to calculate ΔH for a reaction",
          "Explain why Hess's Law works (conservation of energy)",
          "Calculate bond energy from enthalpy data",
          "Draw an enthalpy cycle diagram for a given reaction"
        ],
        materials: "Enthalpy data table, calculator"
      },
      { 
        id: "y12chem-ex3-2", 
        title: "Reaction Rates", 
        type: "Analysis",
        instructions: "Analyse factors affecting reaction rates.",
        tasks: [
          "Explain how temperature affects reaction rate using collision theory",
          "Describe the effect of concentration on reaction rate with graphs",
          "Explain how a catalyst increases reaction rate",
          "Draw energy profile diagrams for catalyzed and uncatalyzed reactions",
          "Predict rate changes when surface area is increased"
        ],
        materials: "Graph paper"
      },
      { 
        id: "y12chem-ex3-3", 
        title: "Equilibrium Shifts", 
        type: "Analysis",
        instructions: "Apply Le Chatelier's Principle to predict equilibrium shifts.",
        tasks: [
          "For N₂ + 3H₂ ⇌ 2NH₃ (ΔH = -92kJ), predict effect of: increasing pressure, temperature, removing NH₃",
          "Explain why high pressure is used in the Haber Process",
          "Describe the compromise conditions in the Contact Process",
          "Calculate Kc from equilibrium concentrations",
          "Explain difference between Kc and Qc"
        ],
        materials: "Calculator"
      }
    ]
  },
  unit4: {
    unitTitle: "Oxidation-Reduction & Electrolysis",
    exercises: [
      { 
        id: "y12chem-ex4-1", 
        title: "Oxidation States", 
        type: "Worksheet",
        instructions: "Assign oxidation states to all atoms in the given compounds.",
        tasks: [
          "Assign oxidation states in: H₂SO₄, KMnO₄, Na₂Cr₂O₇, FeCl₃, HNO₃",
          "Identify which element is oxidized and reduced in: Zn + Cu²⁺ → Zn²⁺ + Cu",
          "Identify the oxidizing and reducing agents",
          "Calculate oxidation state of Mn in MnO₄⁻",
          "Explain rules for assigning oxidation states"
        ],
        materials: "Periodic table"
      },
      { 
        id: "y12chem-ex4-2", 
        title: "Balancing Redox Equations", 
        type: "Calculations",
        instructions: "Balance redox equations using the ion-electron method.",
        tasks: [
          "Balance: Fe²⁺ + MnO₄⁻ → Fe³⁺ + Mn²⁺ (acidic solution)",
          "Balance: Cr₂O₇²⁻ + I⁻ → Cr³⁺ + I₂ (acidic solution)",
          "Write half-equations for oxidation and reduction",
          "Balance electrons transferred in half-equations",
          "Add H₂O and H⁺ to balance oxygen and hydrogen"
        ],
        materials: "None"
      },
      { 
        id: "y12chem-ex4-3", 
        title: "Electrolysis", 
        type: "Practical",
        instructions: "Predict and explain electrolysis products.",
        tasks: [
          "Predict products at anode and cathode for molten NaCl electrolysis",
          "Write electrode equations for electrolysis of dilute H₂SO₄",
          "Explain why Al is extracted by electrolysis not reduction",
          "Draw and label an electrolytic cell",
          "Explain the role of the salt bridge or electrolyte"
        ],
        materials: "Diagram paper"
      }
    ]
  },
  unit5: {
    unitTitle: "Organic Chemistry - Alcohols to Esters",
    exercises: [
      { 
        id: "y12chem-ex5-1", 
        title: "Alcohol Reactions", 
        type: "Worksheet",
        instructions: "Complete exercises on alcohol classifications and reactions.",
        tasks: [
          "Classify butan-1-ol, butan-2-ol, 2-methylpropan-2-ol as primary, secondary, or tertiary",
          "Write equations for complete oxidation of ethanol to ethanoic acid",
          "Describe the test to distinguish aldehydes from ketones",
          "Write the equation for esterification of ethanol with ethanoic acid",
          "Name and draw the structural formula of the ester formed"
        ],
        materials: "Molecular model kit"
      },
      { 
        id: "y12chem-ex5-2", 
        title: "Naming Organic Compounds", 
        type: "Worksheet",
        instructions: "Practice IUPAC nomenclature for organic compounds.",
        tasks: [
          "Name: CH₃CH₂CH(OH)CH₃, CH₃COCH₃, CH₃CH₂COOH, CH₃COOCH₂CH₃",
          "Draw structural formulas for: propan-1-ol, butanone, propanoic acid",
          "Identify functional groups in given structures",
          "Name the products of alcohol oxidation",
          "Draw all isomers of C₄H₁₀O that are alcohols"
        ],
        materials: "None"
      },
      { 
        id: "y12chem-ex5-3", 
        title: "Fats and Soaps", 
        type: "Analysis",
        instructions: "Analyse the structure and reactions of fats.",
        tasks: [
          "Draw a simplified structure of a triglyceride",
          "Explain the difference between saturated and unsaturated fats",
          "Write the equation for saponification",
          "Explain how soap cleans at the molecular level",
          "Describe why unsaturated fats have lower melting points"
        ],
        materials: "Molecular diagrams"
      }
    ]
  }
};

export const year13ChemistryExercises = {
  unit1: {
    unitTitle: "Quantum Chemistry & Molecular Structure",
    exercises: [
      { 
        id: "y13chem-ex1-1", 
        title: "Quantum Numbers Practice", 
        type: "Worksheet",
        instructions: "Assign quantum numbers and write electronic configurations.",
        tasks: [
          "Write the four quantum numbers for the last electron in Fe",
          "Explain the significance of n, l, ml, and ms",
          "Write electronic configurations for Fe, Fe²⁺, Fe³⁺",
          "Explain why transition metals form colored compounds",
          "Draw orbital diagrams showing d-orbital splitting"
        ],
        materials: "Periodic table"
      },
      { 
        id: "y13chem-ex1-2", 
        title: "VSEPR Shapes", 
        type: "Analysis",
        instructions: "Predict molecular shapes using VSEPR theory.",
        tasks: [
          "Predict shapes and bond angles for: BF₃, NH₃, H₂O, SF₆, PCl₅",
          "Explain why H₂O is bent but CO₂ is linear",
          "Draw 3D representations of each molecule",
          "Identify polar and non-polar molecules from their shapes",
          "Explain why CHCl₃ is polar but CCl₄ is non-polar"
        ],
        materials: "Molecular model kit"
      },
      { 
        id: "y13chem-ex1-3", 
        title: "Nuclear Chemistry", 
        type: "Calculations",
        instructions: "Calculate half-lives and write nuclear equations.",
        tasks: [
          "Calculate mass remaining after 3 half-lives (starting with 80g)",
          "Write nuclear equations for α, β, and γ decay of ²³⁸U",
          "Calculate the half-life if 25% remains after 10 days",
          "Explain the difference between fission and fusion",
          "List applications of radioactivity in medicine"
        ],
        materials: "Calculator"
      }
    ]
  },
  unit2: {
    unitTitle: "Acid-Base Equilibria & Buffers",
    exercises: [
      { 
        id: "y13chem-ex2-1", 
        title: "Ka and pH Calculations", 
        type: "Calculations",
        instructions: "Calculate Ka, Kb, and pH for weak acids and bases.",
        tasks: [
          "Calculate pH of 0.1M CH₃COOH (Ka = 1.8 × 10⁻⁵)",
          "Calculate Ka from pH = 2.9 for 0.1M weak acid",
          "Calculate Kb from Ka using Kw = 1 × 10⁻¹⁴",
          "Determine degree of dissociation from Ka and concentration",
          "Compare pH of 0.1M HCl and 0.1M CH₃COOH"
        ],
        materials: "Calculator, log tables"
      },
      { 
        id: "y13chem-ex2-2", 
        title: "Buffer Solutions", 
        type: "Calculations",
        instructions: "Calculate buffer pH and understand buffer capacity.",
        tasks: [
          "Calculate pH of buffer containing 0.2M CH₃COOH and 0.3M CH₃COONa",
          "Use Henderson-Hasselbalch equation: pH = pKa + log([A⁻]/[HA])",
          "Calculate new pH when 0.01M HCl is added to buffer",
          "Explain how buffers resist pH change",
          "Describe the importance of blood buffering (pH 7.35-7.45)"
        ],
        materials: "Calculator"
      },
      { 
        id: "y13chem-ex2-3", 
        title: "Titration Curves", 
        type: "Analysis",
        instructions: "Interpret and sketch titration curves.",
        tasks: [
          "Sketch titration curve for strong acid + strong base",
          "Identify equivalence point and buffer region on curve",
          "Explain why pH at equivalence point differs for different acid-base combinations",
          "Choose appropriate indicator for weak acid-strong base titration",
          "Calculate pH at half-equivalence point"
        ],
        materials: "Graph paper"
      }
    ]
  },
  unit3: {
    unitTitle: "Thermodynamics & Equilibrium Constants",
    exercises: [
      { 
        id: "y13chem-ex3-1", 
        title: "Bond Energy Calculations", 
        type: "Calculations",
        instructions: "Calculate enthalpy changes using bond energies.",
        tasks: [
          "Calculate ΔH for CH₄ + 2O₂ → CO₂ + 2H₂O using bond energies",
          "Explain why bond breaking is endothermic and bond forming is exothermic",
          "Calculate average bond energy from enthalpy of formation",
          "Compare calculated ΔH with standard enthalpy values",
          "Explain limitations of bond energy calculations"
        ],
        materials: "Bond energy table, calculator"
      },
      { 
        id: "y13chem-ex3-2", 
        title: "Kc Calculations", 
        type: "Calculations",
        instructions: "Calculate and apply equilibrium constants.",
        tasks: [
          "Calculate Kc from equilibrium concentrations",
          "Use Kc to predict equilibrium concentrations from initial amounts",
          "Explain how Kc changes with temperature",
          "Compare Qc and Kc to predict reaction direction",
          "Calculate Kp from Kc for gaseous reactions"
        ],
        materials: "Calculator"
      },
      { 
        id: "y13chem-ex3-3", 
        title: "Ksp Problems", 
        type: "Calculations",
        instructions: "Calculate solubility from Ksp and predict precipitation.",
        tasks: [
          "Calculate solubility of CaF₂ from Ksp = 4.0 × 10⁻¹¹",
          "Predict if precipitate forms when solutions are mixed",
          "Calculate Qsp and compare with Ksp",
          "Explain the common ion effect on solubility",
          "Calculate solubility of AgCl in 0.1M NaCl solution"
        ],
        materials: "Calculator"
      }
    ]
  },
  unit4: {
    unitTitle: "Electrochemistry & Galvanic Cells",
    exercises: [
      { 
        id: "y13chem-ex4-1", 
        title: "Galvanic Cells", 
        type: "Practical",
        instructions: "Draw galvanic cells and calculate cell potential.",
        tasks: [
          "Draw a labeled Zn-Cu galvanic cell diagram",
          "Write half-equations for anode and cathode reactions",
          "Calculate E°cell from standard electrode potentials",
          "Predict direction of electron flow",
          "Explain the purpose of the salt bridge"
        ],
        materials: "E° table, diagram paper"
      },
      { 
        id: "y13chem-ex4-2", 
        title: "Cell Notation", 
        type: "Worksheet",
        instructions: "Practice IUPAC cell notation.",
        tasks: [
          "Write cell notation for Zn|Zn²⁺||Cu²⁺|Cu",
          "Interpret cell notation and identify anode/cathode",
          "Draw cell diagram from cell notation",
          "Calculate E°cell from cell notation",
          "Include solution concentrations in notation"
        ],
        materials: "E° table"
      },
      { 
        id: "y13chem-ex4-3", 
        title: "Redox Titrations", 
        type: "Calculations",
        instructions: "Perform calculations for redox titrations.",
        tasks: [
          "Calculate concentration of Fe²⁺ from KMnO₄ titration data",
          "Write balanced equation for Fe²⁺ + MnO₄⁻ reaction",
          "Use mole ratios in redox titration calculations",
          "Explain how to identify endpoint in permanganate titration",
          "Calculate percentage purity from titration data"
        ],
        materials: "Calculator"
      }
    ]
  },
  unit5: {
    unitTitle: "Advanced Organic Chemistry",
    exercises: [
      { 
        id: "y13chem-ex5-1", 
        title: "Amines and Amides", 
        type: "Worksheet",
        instructions: "Name and draw structures of nitrogen compounds.",
        tasks: [
          "Name: CH₃NH₂, (CH₃)₂NH, CH₃CONH₂, C₆H₅NH₂",
          "Draw propylamine, N-methylethanamide, butanamide",
          "Classify amines as primary, secondary, or tertiary",
          "Write equation for formation of amide from amine and acid",
          "Explain why amines are basic"
        ],
        materials: "None"
      },
      { 
        id: "y13chem-ex5-2", 
        title: "Acyl Chloride Reactions", 
        type: "Analysis",
        instructions: "Write equations for acyl chloride reactions.",
        tasks: [
          "Write equations: ethanoyl chloride + water, ethanol, ammonia",
          "Name the products of each reaction",
          "Explain why acyl chlorides are more reactive than carboxylic acids",
          "Describe observations for each reaction",
          "Compare esterification with acyl chloride vs carboxylic acid"
        ],
        materials: "None"
      },
      { 
        id: "y13chem-ex5-3", 
        title: "Carbohydrates", 
        type: "Analysis",
        instructions: "Analyse structure and reactions of carbohydrates.",
        tasks: [
          "Classify: glucose, sucrose, starch, cellulose as mono/di/polysaccharide",
          "Draw the ring structure of glucose (α and β forms)",
          "Describe the Benedict's test for reducing sugars",
          "Explain why sucrose is a non-reducing sugar",
          "Compare structures of starch and cellulose"
        ],
        materials: "Molecular diagrams"
      }
    ]
  }
};

// ============================================
// YEAR 12 & 13 CHEMISTRY TEST BLUEPRINTS
// ============================================
export const year12ChemistryTestBlueprints = {
  unit1: {
    unitTitle: "Unit 1: Ionic Compounds & Periodic Trends",
    strand: "Atomic Structure, Bonding and Related Properties",
    duration: "50 minutes",
    totalMarks: 45,
    slosCovered: ["Ionic formulas", "Electronic configurations", "Periodic trends", "Intermolecular forces"]
  },
  unit2: {
    unitTitle: "Unit 2: Advanced Stoichiometry & Solutions",
    strand: "Quantitative and Aqueous Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    slosCovered: ["Limiting reactants", "Titrations", "pH calculations", "Acid-base theory"]
  },
  unit3: {
    unitTitle: "Unit 3: Thermochemistry & Kinetics",
    strand: "Physical Chemistry",
    duration: "50 minutes",
    totalMarks: 45,
    slosCovered: ["Enthalpy", "Hess's Law", "Reaction rates", "Equilibrium"]
  },
  unit4: {
    unitTitle: "Unit 4: Oxidation-Reduction & Electrolysis",
    strand: "Oxidation and Reduction",
    duration: "50 minutes",
    totalMarks: 45,
    slosCovered: ["Oxidation states", "Redox equations", "Electrolysis", "Corrosion"]
  },
  unit5: {
    unitTitle: "Unit 5: Organic Chemistry - Alcohols to Esters",
    strand: "Organic Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    slosCovered: ["Alcohols", "Aldehydes/Ketones", "Carboxylic acids", "Esters", "Fats"]
  }
};

export const year13ChemistryTestBlueprints = {
  unit1: {
    unitTitle: "Unit 1: Quantum Chemistry & Molecular Structure",
    strand: "Atomic Structure, Bonding and Related Properties",
    duration: "55 minutes",
    totalMarks: 50,
    slosCovered: ["Atomic spectra", "Quantum numbers", "VSEPR", "Nuclear chemistry"]
  },
  unit2: {
    unitTitle: "Unit 2: Acid-Base Equilibria & Buffers",
    strand: "Quantitative and Aqueous Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    slosCovered: ["Ka/Kb", "Buffer solutions", "Titration curves", "Henderson-Hasselbalch"]
  },
  unit3: {
    unitTitle: "Unit 3: Thermodynamics & Equilibrium Constants",
    strand: "Physical Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    slosCovered: ["Bond energies", "Kc", "Ksp", "Thermodynamics"]
  },
  unit4: {
    unitTitle: "Unit 4: Electrochemistry & Galvanic Cells",
    strand: "Oxidation and Reduction",
    duration: "55 minutes",
    totalMarks: 50,
    slosCovered: ["Galvanic cells", "E°cell", "Cell notation", "Redox titrations"]
  },
  unit5: {
    unitTitle: "Unit 5: Advanced Organic Chemistry",
    strand: "Organic Chemistry",
    duration: "55 minutes",
    totalMarks: 50,
    slosCovered: ["Amines", "Acyl chlorides", "Carbohydrates", "Fats"]
  }
};

export default {
  year11ChemistryUnitTests,
  year11ChemistryExercises,
  year12ChemistryUnitTests,
  year12ChemistryExercises,
  year13ChemistryUnitTests,
  year13ChemistryExercises,
  chemistryResources,
  chemistryTestBlueprints,
  year12ChemistryTestBlueprints,
  year13ChemistryTestBlueprints,
  chemistryAssessment
};
